// F15: ④ 初期ワールド設計 — 物語とエンジン能力から開始ワールドの実寸配置を設計する。
// 出力は world-design.json（後段へ強制反映する構造化設計）と world-design.md（人間の確認用）。
import { writeFileSync } from 'node:fs';
import { callLlmJson } from './llm-client.mjs';
import { pipelineConfig, fillTemplate, toSnakeId, str, asArray } from './story-shared.mjs';
import { generationLanguageInstruction, localizedForGeneration } from './story-source.mjs';
import { projectPaths, readProjectMeta, requireAnalysis, recordStep, addUsage } from './story-project.mjs';
import { requireGameDir } from './games.mjs';
import { writeJson } from './json-utils.mjs';
import { auditLog } from './logger.mjs';
import {
    designGroundInfo, surfaceInfoFromFills, overworldBandHeight, isWalkableSurface, rectsIntersect,
    scatterPointsInShape,
} from './story-world-geometry.mjs';
import { loadBlueprintCatalog, renderBlueprintCatalog, blueprintFootprintRect } from './story-blueprints.mjs';

/**
 * world-design.json の現行フォーマット。
 * 2.0: 実寸設計（Codex 改修）。2.1: 段丘 boundaries/stairs・groves（木立）・道路 range・
 * 幾何ベースの Y 確定・landmarks・cityWall。2.2: 解析済み非プレイアブル cast の必須配置。
 * 2.3: 海・川・湖・温泉・毒沼・マグマの液体地形。
 * 2.4: 敵の物語上の格と配置単位の表示倍率（強敵1.5〜3倍）。
 * 2.5: 歩行エリアの非水地盤保証と、実地表に一致する開始地点。
 * 2.6: 物語上重要な座標へ接近した時の表示パターン付きイベント案。
 * いずれも構造追加のみで、旧版は⑤実行時に無償で自動移行される。
 */
export const WORLD_DESIGN_FORMAT_VERSION = '2.6';

const copy = (value) => JSON.parse(JSON.stringify(value));
const finite = (value, fallback) => (Number.isFinite(Number(value)) ? Number(value) : fallback);
const integer = (value, fallback) => Math.floor(finite(value, fallback));
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function designConfig() {
    return pipelineConfig().worldDesign;
}

function enemyVisualScaleConfig() {
    return pipelineConfig().enemyVisualScale ?? {};
}

function normalizeEnemyRank(value) {
    const cfg = enemyVisualScaleConfig();
    const allowed = asArray(cfg.allowedRanks);
    if (allowed.includes(value)) return value;
    if (allowed.includes(cfg.defaultRank)) return cfg.defaultRank;
    return allowed[0] ?? '';
}

/** 通常敵は基準のまま、強敵だけJSON設定の範囲内へ正規化する。 */
function normalizeEnemyDisplayScale(rank, value) {
    const cfg = enemyVisualScaleConfig();
    const normalScale = finite(cfg.normalScale, 1);
    if (rank === normalizeEnemyRank(cfg.defaultRank)) return normalScale;
    const min = finite(cfg.strongScaleRange?.min, normalScale);
    const max = Math.max(min, finite(cfg.strongScaleRange?.max, min));
    const fallback = finite(cfg.defaultScaleByRank?.[rank], min);
    const digits = Math.max(0, integer(cfg.roundDigits, 2));
    const factor = 10 ** digits;
    return Math.round(clamp(finite(value, fallback), min, max) * factor) / factor;
}

/** プロジェクトに紐づくゲームのブループリントカタログ（②未実行・ゲーム欠損時は空）。 */
export function projectBlueprintCatalog(projectId) {
    try {
        const meta = readProjectMeta(projectId);
        if (!meta.gameId) return [];
        return loadBlueprintCatalog(requireGameDir(meta.gameId));
    } catch {
        return [];
    }
}

/** ランドマークの XZ フットプリント矩形（回避・重なり検査・lint 共通）。 */
export function landmarkFootprintRect(landmark, engine) {
    const margin = engine.landmark.footprintMargin;
    if (landmark.kind === 'castle') {
        const template = engine.landmark.castle.template;
        const reach = Math.ceil((template.moat.innerRadius + template.moat.width) * landmark.scale) + margin;
        return {
            minX: engine.world.center.x - reach,
            maxX: engine.world.center.x + reach,
            minZ: landmark.positionZ - reach,
            maxZ: landmark.positionZ + reach,
        };
    }
    if (landmark.kind === 'tower') {
        const reach = landmark.baseRadius + margin;
        return {
            minX: landmark.position[0] - reach,
            maxX: landmark.position[0] + reach + 1,
            minZ: landmark.position[1] - reach,
            maxZ: landmark.position[1] + reach + 1,
        };
    }
    if (landmark.kind === 'blueprint') {
        const rect = blueprintFootprintRect({ size: landmark.size, originOffset: landmark.originOffset }, landmark.position, landmark.rotation);
        return { minX: rect.minX - margin, maxX: rect.maxX + margin, minZ: rect.minZ - margin, maxZ: rect.maxZ + margin };
    }
    return {
        minX: landmark.bounds.minX - margin,
        maxX: landmark.bounds.maxX + margin,
        minZ: landmark.bounds.minZ - margin,
        maxZ: landmark.bounds.maxZ + margin,
    };
}

/**
 * landmarks（シンボル建造物）の正規化。
 * castle=エンジン城生成器 / tower=塔合成 / blueprint=同梱カタログ / custom=自由 fill 造形。
 * 空中都市・宇宙艦向けに altitude（任意高度）を許可する。
 */
function normalizeLandmarks(raw, engine, world, fallbackBounds, blueprints, notes) {
    const cfg = engine.landmark;
    const safeBlocks = new Set(engine.safeBlocks);
    const safeBlock = (value, fallback) => (safeBlocks.has(str(value)) ? str(value) : fallback);
    const landmarks = [];
    for (const [index, entry] of asArray(raw).slice(0, cfg.maxLandmarks).entries()) {
        if (!entry || typeof entry !== 'object') continue;
        const kind = ['castle', 'tower', 'blueprint', 'custom'].includes(entry.kind) ? entry.kind : null;
        if (!kind) continue;
        const base = {
            id: toSnakeId(entry.id ?? entry.nameEn ?? entry.nameJa, `landmark_${index + 1}`),
            kind,
            nameJa: str(entry.nameJa) || str(entry.name) || `ランドマーク${index + 1}`,
            nameEn: str(entry.nameEn) || `Landmark ${index + 1}`,
            purpose: str(entry.purpose),
        };
        const altitude = Number.isFinite(Number(entry.altitude))
            ? clamp(integer(entry.altitude, 0), 1, world.height - 8)
            : null;
        const point = (fallbackX, fallbackZ) => [
            clamp(integer(asArray(entry.position)[0], fallbackX), 0, world.width - 1),
            clamp(integer(asArray(entry.position)[1], fallbackZ), 0, world.depth - 1),
        ];
        if (kind === 'castle') {
            if (landmarks.some((other) => other.kind === 'castle')) {
                notes.push(`ランドマーク ${base.id}: 城はエンジン基準座標が単一のため1基のみ。2基目は tower / blueprint / custom で表現する`);
                continue;
            }
            const scale = clamp(finite(entry.scale, 1), cfg.castle.scaleRange[0], cfg.castle.scaleRange[1]);
            const reach = Math.ceil((cfg.castle.template.moat.innerRadius + cfg.castle.template.moat.width + 6) * scale);
            landmarks.push({
                ...base,
                scale,
                positionZ: clamp(integer(entry.positionZ, reach + 12), reach, world.depth - reach),
                moat: entry.moat !== false,
            });
        } else if (kind === 'tower') {
            const t = cfg.tower;
            const baseRadius = clamp(integer(entry.baseRadius, t.defaults.baseRadius), t.radiusRange[0], t.radiusRange[1]);
            landmarks.push({
                ...base,
                position: point(world.center.x, world.center.z),
                baseRadius,
                topRadius: clamp(integer(entry.topRadius, Math.max(2, baseRadius - 3)), 2, baseRadius),
                height: clamp(integer(entry.height, t.defaults.height), t.heightRange[0], t.heightRange[1]),
                floorHeight: clamp(integer(entry.floorHeight, t.defaults.floorHeight), t.floorHeightRange[0], t.floorHeightRange[1]),
                topStyle: t.topStyles.includes(entry.topStyle) ? entry.topStyle : t.defaults.topStyle,
                balconies: entry.balconies !== false,
                altitude,
                materials: {
                    wall: safeBlock(entry.materials?.wall, t.defaults.wall),
                    window: safeBlock(entry.materials?.window, t.defaults.window),
                    accent: safeBlock(entry.materials?.accent, t.defaults.accent),
                    top: safeBlock(entry.materials?.top, t.defaults.top),
                },
            });
        } else if (kind === 'blueprint') {
            const catalogEntry = blueprints.find((candidate) => candidate.id === str(entry.blueprintId));
            if (!catalogEntry) {
                notes.push(`ランドマーク ${base.id}: blueprintId「${str(entry.blueprintId)}」はカタログに無いため除外（custom の fills で造形するか、カタログの id を使う）`);
                continue;
            }
            const usedBoxes = landmarks
                .filter((other) => other.kind === 'blueprint')
                .reduce((sum, other) => sum + (blueprints.find((candidate) => candidate.id === other.blueprintId)?.boxCount ?? 0), 0);
            if (usedBoxes + catalogEntry.boxCount > cfg.maxGeneratedFillsTotal) {
                notes.push(`ランドマーク ${base.id}: ブループリント合計が fill 予算（${cfg.maxGeneratedFillsTotal}）を超えるため除外`);
                continue;
            }
            const materialOverrides = {};
            for (const [from, to] of Object.entries(entry.materialOverrides ?? {}).slice(0, 12)) {
                if (safeBlocks.has(str(from)) && safeBlocks.has(str(to))) materialOverrides[str(from)] = str(to);
            }
            landmarks.push({
                ...base,
                blueprintId: catalogEntry.id,
                size: catalogEntry.size ?? [1, 1, 1],
                originOffset: catalogEntry.originOffset,
                position: point(world.center.x, world.center.z),
                rotation: [0, 90, 180, 270].includes(entry.rotation) ? entry.rotation : 0,
                altitude,
                materialOverrides,
            });
        } else {
            const bounds = normalizeBounds(entry.bounds, fallbackBounds, world);
            landmarks.push({
                ...base,
                bounds,
                altitude,
                fills: asArray(entry.fills).slice(0, cfg.maxCustomFillsPerLandmark)
                    .map((fill, fillIndex) => normalizeFill(fill, fillIndex, `${base.id}_fill`, engine, 'STONE'))
                    .filter(Boolean),
            });
        }
    }
    return landmarks;
}

/** 都市外周壁（エンジンの perimeter wall）。中心正方形リング＋監視塔。overworld 専用。 */
function normalizeCityWall(raw, engine, generator, notes) {
    const cfg = engine.cityWall;
    const safeBlocks = new Set(engine.safeBlocks);
    const safeBlock = (value, fallback) => (safeBlocks.has(str(value)) ? str(value) : fallback);
    const requested = raw?.enabled === true;
    if (requested && generator !== 'overworld') {
        notes.push('cityWall はエンジンの地層構造を前提とするため overworld 専用。void 世界の城壁は detailFills / custom ランドマークで表現する');
    }
    const enabled = requested && generator === 'overworld';
    return {
        enabled,
        position: clamp(integer(raw?.position, cfg.defaults.position), cfg.positionRange[0], cfg.positionRange[1]),
        height: clamp(integer(raw?.height, cfg.defaults.height), 4, 40),
        thickness: clamp(integer(raw?.thickness, cfg.defaults.thickness), 2, 8),
        towerBaseSize: clamp(integer(raw?.towerBaseSize, cfg.defaults.towerBaseSize), 4, 20),
        towerHeight: clamp(integer(raw?.towerHeight, cfg.defaults.towerHeight), 6, 60),
        wallBlockType: safeBlock(raw?.wallBlockType, cfg.defaults.wallBlockType),
        towerBlockType: safeBlock(raw?.towerBlockType, cfg.defaults.towerBlockType),
        roofBlockType: safeBlock(raw?.roofBlockType, cfg.defaults.roofBlockType),
        groundBlockType: safeBlock(raw?.groundBlockType, cfg.defaults.groundBlockType),
        towerGroundBlockType: safeBlock(raw?.towerGroundBlockType, cfg.defaults.towerGroundBlockType),
        gate: { enabled: enabled && raw?.gate?.enabled !== false },
    };
}

function templateCatalog() {
    return pipelineConfig().startWorldBases?.options ?? [];
}

function promptTemplateCatalog(analysis) {
    return JSON.stringify({
        analysisSelected: analysis.game.startWorldBase,
        options: templateCatalog().map(({ id, label, hint, defaultGenerator, sky, defaultWeather, forcedWeather, strengths, limitations, foundationFills }) => ({
            id, label, hint, defaultGenerator, sky, defaultWeather, forcedWeather, strengths, limitations, foundationFills,
        })),
    }, null, 1);
}

export async function runWorldDesign(projectId, { onLog = () => {}, signal = null } = {}) {
    const analysis = requireAnalysis(projectId);
    const cfg = designConfig();
    const p = projectPaths(projectId);
    const startMapId = analysis.game.startMapId;
    const analysisDigest = {
        game: {
            title: analysis.game.title,
            genre: analysis.game.genre,
            worldSetting: analysis.game.worldSetting,
            startWorldBase: analysis.game.startWorldBase,
            generationLanguage: analysis.game.generationLanguage,
        },
        storySummary: {
            maps: analysis.summary.maps,
            buildings: analysis.summary.buildings,
            enemies: analysis.summary.enemies,
            events: analysis.summary.events,
        },
        startMap: analysis.world.startMap,
        futureMaps: analysis.world.futureMaps,
        cast: analysis.cast.map((c) => ({
            id: c.id,
            role: c.role,
            name: localizedForGeneration(c.name, analysis.game.generationLanguage, c.id),
            appearance: c.appearance,
            humanoid: c.humanoid,
            locationHint: c.locationHint,
            ...(c.role === 'enemy' ? { enemyRank: normalizeEnemyRank(c.enemyRank) } : {}),
        })),
    };
    signal?.throwIfAborted();
    onLog(`開始ワールド「${startMapId}」の縮尺・地形・シンボル建造物・生活圏・生息地を設計します……`);
    const blueprints = projectBlueprintCatalog(projectId);
    const normalizeOptions = { blueprints };
    const prompt = fillTemplate(cfg.prompt, {
        startMapId,
        analysisDigest: JSON.stringify(analysisDigest, null, 1),
        templateCatalog: promptTemplateCatalog(analysis),
        blueprintCatalog: renderBlueprintCatalog(blueprints),
        engineCapabilities: JSON.stringify({
            world: cfg.engine.world,
            generators: cfg.engine.generatorCapabilities,
            areaTypes: cfg.engine.areaTypes,
            safeBlocks: cfg.engine.safeBlocks,
            building: cfg.engine.building,
        }, null, 1),
        generationLanguageInstruction: generationLanguageInstruction(analysis.game.generationLanguage),
    });
    let r = await callLlmJson('worldDesign', {
        system: cfg.system,
        prompt,
        onLog,
    });
    addUsage(projectId, r.usage, r.calls);
    let design = normalizeWorldDesign(r.json ?? {}, analysis, normalizeOptions);
    let qualityIssues = worldDesignQualityIssues(design, analysis);
    if (qualityIssues.length > 0) {
        onLog(`  設計品質チェック: ${qualityIssues.length}件を補うため1回だけ再設計します`);
        for (const issue of qualityIssues) onLog(`    - ${issue}`);
        const retry = await callLlmJson('worldDesign', {
            system: cfg.system,
            prompt: [
                prompt,
                '',
                '## 前回設計の品質不足（すべて解消して完全なJSONを再出力）',
                ...qualityIssues.map((issue) => `- ${issue}`),
                '',
                '## 前回の設計JSON（良い部分は維持し、不足だけでなく相互配置も再確認する）',
                JSON.stringify(r.json ?? {}, null, 1),
            ].join('\n'),
            onLog,
        });
        addUsage(projectId, retry.usage, retry.calls);
        r = retry;
        design = normalizeWorldDesign(retry.json ?? {}, analysis, normalizeOptions);
        qualityIssues = worldDesignQualityIssues(design, analysis);
        if (qualityIssues.length > 0) {
            design.notes.push(`自動品質チェックの残件: ${qualityIssues.join(' / ')}`);
            onLog(`  ⚠ 再設計後も品質チェック ${qualityIssues.length}件が残りました。設計メモへ記録して続行します`);
        }
    }
    writeJson(p.worldDesign, design);
    writeFileSync(p.worldDesignMd, renderWorldDesignMd(design), 'utf-8');
    recordStep(projectId, 'worldDesign', {
        ok: true,
        areas: design.areas.length,
        buildings: design.buildings.length,
        buildingClusters: design.buildingClusters.length,
        landmarks: design.landmarks.length,
        detailFills: design.detailFills.length,
        groves: design.groves.length,
        enemies: design.enemyPlacements.filter((entry) => entry.enabled).length,
        model: r.model,
    });
    auditLog('story-world-design', {
        projectId,
        foundation: design.foundation.baseTemplateId,
        mode: design.foundation.mode,
        generator: design.foundation.generator,
        areas: design.areas.length,
        buildings: design.buildings.length,
        buildingClusters: design.buildingClusters.length,
    });
    onLog(`✔ ワールド設計完了: ${design.foundation.baseTemplateId}/${design.foundation.mode}/${design.foundation.generator} / エリア ${design.areas.length} / 主要建物 ${design.buildings.length} / 建物群 ${design.buildingClusters.length} / シンボル建造物 ${design.landmarks.length} / 景観 ${design.detailFills.length}`);
    return design;
}

/** 物語再現に必要な設計要素が欠けたまま⑤へ進むことを防ぐための品質チェック。 */
export function worldDesignQualityIssues(design, analysis) {
    const issues = [];
    if (!design.scale.rationale) issues.push('各地域のブロック寸法と1000×1000×100の使い方を scale.rationale で説明する');
    if (design.scale.sourceEvidence.length === 0) issues.push('規模判断に使った物語上の根拠を scale.sourceEvidence に入れる');
    if (!design.foundation.selectionReason) issues.push('全テンプレートを比較した選定理由を foundation.selectionReason に入れる');
    if (!design.environment.skyMood) issues.push('物語に合う空・星・雲を environment.skyMood に設計する');
    const naturalCount = design.terrain.features.length
        + design.terrain.water.rivers.length + design.terrain.water.canals.length
        + design.terrain.roads.length + design.terrain.vegetation.forestZones.length
        + design.terrain.vegetation.flowerZones.length + design.detailFills.length;
    if (naturalCount === 0) issues.push('自然地形・道路・生活景観のいずれも空にせず、この物語に必要な環境を設計する');
    const storyBuildings = asArray(analysis.world?.startMap?.buildings).length > 0 || !!str(analysis.summary?.buildings);
    if (storyBuildings && design.buildings.length + design.buildingClusters.length === 0) {
        issues.push('物語上の建物と生活密度を buildings / buildingClusters に設計する');
    }
    if (design.buildings.length + design.buildingClusters.length > 0
        && design.terrain.roads.length === 0 && design.detailFills.length === 0) {
        issues.push('人の住む環境を結ぶ道路・路地・庭・水路・橋等を roads または detailFills に設計する');
    }
    const storyEnemies = asArray(analysis.cast).filter((cast) => cast.role === 'enemy');
    const placedEnemyCastIds = new Set(design.enemyPlacements
        .filter((entry) => entry.enabled !== false)
        .map((entry) => entry.castId));
    for (const enemy of storyEnemies) {
        if (!placedEnemyCastIds.has(enemy.id)) {
            issues.push(`物語の敵 ${enemy.id} を enemyPlacements に配置する（解析済みの敵は全員必ず配置する）`);
        }
    }
    for (const enemy of design.enemyPlacements.filter((entry) => entry.enabled)) {
        if (enemy.habitatRequirements.length === 0) issues.push(`${enemy.name || enemy.castId} の habitatRequirements に必要な地形・雰囲気を記載する`);
    }
    const hasImportantPlaceCandidates = design.landmarks.length > 0
        || design.buildings.length > 0
        || !!str(analysis.summary?.events);
    if (hasImportantPlaceCandidates && design.eventIdeas.length === 0) {
        issues.push('物語上重要な入口・建物・ランドマーク・出来事の場所を eventIdeas に設計する');
    }
    if (design.eventIdeas.length > 0 && !design.eventIdeas.some((event) => event.trigger === 'proximity')) {
        issues.push('広域の area_enter だけでなく、重要な特定地点へ近づく proximity イベントを eventIdeas に含める');
    }
    for (const event of design.eventIdeas) {
        if (!event.message && asArray(event.lines).length === 0) issues.push(`イベント ${event.id} に表示する message または lines を設定する`);
        if (!event.storyBasis) issues.push(`イベント ${event.id} の重要性を判断した物語上の根拠を storyBasis に記載する`);
    }
    if (design.foundation.baseTemplateId === 'sea_world') {
        const hasWater = design.foundation.generator === 'overworld'
            ? design.areas.some((area) => area.surfaceBlock === 'WATER')
            : design.foundation.fills.some((fill) => fill.blockType === 'WATER');
        const hasLand = design.areas.some((area) => !['WATER', 'TRANSPARENT_BLOCK', 'SPACE_BLOCK'].includes(area.surfaceBlock))
            || design.foundation.fills.some((fill) => !['AIR', 'BEDROCK', 'WATER', 'TRANSPARENT_BLOCK', 'SPACE_BLOCK'].includes(fill.blockType));
        if (!hasWater || !hasLand) issues.push('海ワールドに海と居住可能な島/浜辺/海岸の両方を設計する');
    }
    if (design.foundation.baseTemplateId === 'space_world' && design.environment.weather.type !== 'star') {
        issues.push('宇宙ワールドは星空が見える weather.type=star にする');
    }
    issues.push(...worldDesignGeometryIssues(design));
    return [...new Set(issues)];
}

/**
 * 決定的に計算できる幾何整合チェック。スポーン・NPC・建物の足場、港と水域の隣接、
 * 敵生息地と地形の一致を設計データだけで検証し、再設計プロンプトへ具体的な問題を渡す。
 */
export function worldDesignGeometryIssues(design) {
    const issues = [];
    const engine = designConfig().engine;
    const placement = engine.placement;
    const unwalkable = placement.unwalkableSurfaceBlocks;
    const waterLike = new Set([...placement.waterLikeBlocks, 'WATER']);
    const ground = (x, z) => designGroundInfo(design, x, z);
    const landmarks = asArray(design.landmarks);
    const landmarkRects = landmarks.map((landmark) => ({ landmark, rect: landmarkFootprintRect(landmark, engine) }));
    // ランドマークはエンジン・fill が実体の足場を作るため、その上のスポーン・NPC・建物は
    // designGroundInfo（設計 fills と地形だけを見る）では空中と誤判定される → 免除する
    const onLandmark = (x, z) => landmarkRects.some(({ rect }) => x >= rect.minX && x < rect.maxX && z >= rect.minZ && z < rect.maxZ);

    const spawnInfo = ground(design.spawn[0], design.spawn[2]);
    if (!isWalkableSurface(spawnInfo, unwalkable) && !onLandmark(design.spawn[0], design.spawn[2])) {
        issues.push(`スポーン地点 [${design.spawn[0]}, ${design.spawn[2]}] の地表が${spawnInfo ? spawnInfo.blockType : '空中'}になっている。歩ける地面の上へ移すか、地面の fill を設計する`);
    }
    for (const npc of design.npcPlacements) {
        const info = ground(npc.position[0], npc.position[2]);
        if (!isWalkableSurface(info, unwalkable) && !onLandmark(npc.position[0], npc.position[2])) {
            issues.push(`NPC ${npc.castId} の位置 [${npc.position[0]}, ${npc.position[2]}] が${info ? info.blockType : '空中'}の上になっている`);
        }
    }
    for (const building of design.buildings) {
        const cx = building.position[0] + Math.floor(building.width / 2);
        const cz = building.position[2] + Math.floor(building.depth / 2);
        const info = ground(cx, cz);
        if (!isWalkableSurface(info, unwalkable) && !onLandmark(cx, cz)) {
            issues.push(`建物 ${building.nameJa}（${building.id}）の中心が${info ? info.blockType : '空中'}の上にある。位置か地面の fill を見直す`);
        }
    }
    // ランドマーク同士・主要建築との重なり、および接地の検査
    for (let i = 0; i < landmarkRects.length; i++) {
        for (let j = i + 1; j < landmarkRects.length; j++) {
            if (rectsIntersect(landmarkRects[i].rect, landmarkRects[j].rect)) {
                issues.push(`ランドマーク ${landmarkRects[i].landmark.nameJa} と ${landmarkRects[j].landmark.nameJa} の敷地が重なっている。位置を離す`);
            }
        }
        const { landmark, rect } = landmarkRects[i];
        for (const building of design.buildings) {
            const buildingRect = {
                minX: building.position[0], maxX: building.position[0] + building.width,
                minZ: building.position[2], maxZ: building.position[2] + building.depth,
            };
            if (rectsIntersect(rect, buildingRect)) {
                issues.push(`建物 ${building.nameJa} がランドマーク ${landmark.nameJa} の敷地内にある。敷地の外へ移す`);
            }
        }
        // 接地確認: altitude 指定（空中・宇宙）は免除。地上型は中心に歩ける地面（透明床・水面以外）が必要
        if ((landmark.kind === 'tower' || landmark.kind === 'blueprint') && landmark.altitude == null) {
            const info = ground(landmark.position[0], landmark.position[1]);
            if (!isWalkableSurface(info, unwalkable)) {
                issues.push(`ランドマーク ${landmark.nameJa} の足元に地面が無い（${info ? info.blockType : '空中'}）。地面の fill を設計するか、空中に浮かべるなら altitude を指定する`);
            }
        }
    }
    // 物語がシンボル建造物を示唆しているのに landmarks が空の場合は設計を促す
    if (landmarks.length === 0) {
        const hints = engine.landmark.hintKeywords ?? {};
        const storyText = [
            str(design.concept),
            ...design.areas.map((area) => `${area.nameJa} ${area.purpose}`),
            ...design.buildings.map((building) => building.nameJa),
        ].join(' ');
        for (const [kind, words] of Object.entries(hints)) {
            if (asArray(words).some((word) => storyText.includes(word))) {
                issues.push(`物語に「${asArray(words).find((word) => storyText.includes(word))}」が登場する。landmarks（kind=${kind} / blueprint / custom）で象徴的な建造物を設計するか、不要な理由を notes に書く`);
                break;
            }
        }
    }
    // 港・海岸エリアは実際に水域と接している必要がある（名称だけの港を防ぐ）
    for (const area of design.areas.filter((entry) => ['harbor', 'coast'].includes(entry.type))) {
        const b = area.bounds;
        let touchesWater = false;
        for (let i = 0; i <= 4 && !touchesWater; i++) {
            for (let j = 0; j <= 4 && !touchesWater; j++) {
                const x = b.minX + Math.floor(((b.maxX - 1 - b.minX) * i) / 4);
                const z = b.minZ + Math.floor(((b.maxZ - 1 - b.minZ) * j) / 4);
                const info = ground(x, z);
                if (info && waterLike.has(info.blockType)) touchesWater = true;
            }
        }
        if (!touchesWater) issues.push(`${area.nameJa}（${area.type}）の範囲内に水面が1点もない。海・湖・川の水域と接続する`);
    }
    // 敵の生息地タイプと spawnBounds 内の実地形の一致
    const waterWords = asArray(placement.waterHabitatKeywords);
    const forestWords = asArray(placement.forestHabitatKeywords);
    const waterAreaTypes = new Set(asArray(placement.waterAreaTypes));
    const forestAreaTypes = new Set(asArray(placement.forestAreaTypes));
    for (const enemy of design.enemyPlacements.filter((entry) => entry.enabled)) {
        const habitat = `${enemy.habitatType}`;
        const area = design.areas.find((entry) => entry.id === enemy.areaId);
        const b = enemy.spawnBounds;
        const sample = (predicate) => {
            for (let i = 0; i <= 3; i++) {
                for (let j = 0; j <= 3; j++) {
                    const x = b.minX + Math.floor(((b.maxX - 1 - b.minX) * i) / 3);
                    const z = b.minZ + Math.floor(((b.maxZ - 1 - b.minZ) * j) / 3);
                    if (predicate(x, z)) return true;
                }
            }
            return false;
        };
        if (waterWords.some((word) => habitat.includes(word))) {
            const ok = (area && waterAreaTypes.has(area.type))
                || sample((x, z) => waterLike.has(ground(x, z)?.blockType));
            if (!ok) issues.push(`敵 ${enemy.name || enemy.castId} の生息地（${enemy.habitatType}）に対応する水域が spawnBounds 内にない`);
        }
        if (forestWords.some((word) => habitat.includes(word))) {
            const zones = design.terrain.vegetation.forestZones;
            const groves = asArray(design.groves);
            const overlapsForest = (area && forestAreaTypes.has(area.type))
                || zones.some((zone) => {
                    const dx = Math.max(b.minX, Math.min(b.maxX - 1, zone.center[0])) - zone.center[0];
                    const dz = Math.max(b.minZ, Math.min(b.maxZ - 1, zone.center[1])) - zone.center[1];
                    return dx * dx + dz * dz <= zone.radius * zone.radius;
                })
                || groves.some((grove) => {
                    if (grove.shape === 'rect') {
                        return grove.bounds.minX < b.maxX && grove.bounds.maxX > b.minX && grove.bounds.minZ < b.maxZ && grove.bounds.maxZ > b.minZ;
                    }
                    const dx = Math.max(b.minX, Math.min(b.maxX - 1, grove.center.x)) - grove.center.x;
                    const dz = Math.max(b.minZ, Math.min(b.maxZ - 1, grove.center.z)) - grove.center.z;
                    return dx * dx + dz * dz <= grove.radius * grove.radius;
                });
            if (!overlapsForest) issues.push(`敵 ${enemy.name || enemy.castId} の生息地（${enemy.habitatType}）に対応する森・木立が spawnBounds 周辺にない。forestZones か groves を設計する`);
        }
    }
    // void では terrain.water が地形に反映されない（水域は fills で表現する）
    if (design.foundation.generator === 'void') {
        const water = design.terrain.water;
        if (water.rivers.length + water.canals.length + water.ringCanals.length > 0) {
            issues.push('void generator では terrain.water（rivers/canals/ringCanals）が反映されない。川・水路は foundation.fills / detailFills の WATER で表現する');
        }
    }
    return issues;
}

/** UI で編集されたワールド設計を正規化して保存する（md も再生成）。 */
export function saveWorldDesign(projectId, design) {
    const analysis = requireAnalysis(projectId);
    const normalized = normalizeWorldDesign(design ?? {}, analysis, { blueprints: projectBlueprintCatalog(projectId) });
    const p = projectPaths(projectId);
    writeJson(p.worldDesign, normalized);
    writeFileSync(p.worldDesignMd, renderWorldDesignMd(normalized), 'utf-8');
    return normalized;
}

function normalizeBounds(raw, fallback, world) {
    const source = raw && typeof raw === 'object' ? raw : fallback;
    let minX = clamp(integer(source?.minX, fallback.minX), 0, world.width - 1);
    let maxX = clamp(integer(source?.maxX, fallback.maxX), minX + 1, world.width);
    let minZ = clamp(integer(source?.minZ, fallback.minZ), 0, world.depth - 1);
    let maxZ = clamp(integer(source?.maxZ, fallback.maxZ), minZ + 1, world.depth);
    if (maxX <= minX) maxX = Math.min(world.width, minX + 1);
    if (maxZ <= minZ) maxZ = Math.min(world.depth, minZ + 1);
    return { minX, maxX, minZ, maxZ };
}

function centeredBounds(world, defaults) {
    const width = Math.min(world.designWidth, defaults.primaryAreaWidth);
    const depth = Math.min(world.designDepth, defaults.primaryAreaDepth);
    return {
        minX: Math.floor(world.center.x - width / 2),
        maxX: Math.floor(world.center.x + width / 2),
        minZ: Math.floor(world.center.z - depth / 2),
        maxZ: Math.floor(world.center.z + depth / 2),
    };
}

function contains(bounds, x, z) {
    return x >= bounds.minX && x < bounds.maxX && z >= bounds.minZ && z < bounds.maxZ;
}

/** 優先座標→中央→決定的散布の順で、範囲内の歩行可能な実地表を探す。 */
function findWalkablePoint({ bounds, preferred, groundAt, unwalkable, seedText }) {
    const center = {
        x: Math.floor((bounds.minX + bounds.maxX) / 2),
        z: Math.floor((bounds.minZ + bounds.maxZ) / 2),
    };
    const candidates = [];
    if (preferred && contains(bounds, preferred.x, preferred.z)) candidates.push(preferred);
    candidates.push(center);
    for (const point of candidates) {
        const info = groundAt(point.x, point.z);
        if (isWalkableSurface(info, unwalkable)) return { ...point, info };
    }
    const scattered = scatterPointsInShape({
        seedText,
        count: 64,
        maxAttempts: 1024,
        shape: { kind: 'rect', ...bounds },
        accept: (x, z) => isWalkableSurface(groundAt(x, z), unwalkable),
    });
    if (scattered.length === 0) return null;
    const point = scattered[0];
    return { ...point, info: groundAt(point.x, point.z) };
}

function normalizeFill(raw, index, prefix, engine, fallbackBlock = 'BEDROCK') {
    if (!raw || typeof raw !== 'object') return null;
    const world = engine.world;
    const blocks = new Set(engine.safeBlocks);
    const safeBlock = (value, fallback) => blocks.has(str(value)) ? str(value) : fallback;
    const shape = ['rect', 'circle', 'ellipse'].includes(raw.shape) ? raw.shape : 'rect';
    const rawPos = asArray(raw.pos);
    const y = clamp(integer(rawPos[1], 0), 0, world.height - 1);
    const result = {
        id: toSnakeId(raw.id, `${prefix}_${index + 1}`),
        purpose: str(raw.purpose) || str(raw.comment),
        shape,
        blockType: safeBlock(raw.blockType, fallbackBlock),
        pos: [
            clamp(integer(rawPos[0], shape === 'rect' ? 0 : world.center.x), 0, world.width - 1),
            y,
            clamp(integer(rawPos[2], shape === 'rect' ? 0 : world.center.z), 0, world.depth - 1),
        ],
    };
    const blockTypes = asArray(raw.blockTypes).map((value) => safeBlock(value, null)).filter(Boolean);
    if (blockTypes.length) result.blockTypes = [...new Set(blockTypes)];
    if (shape === 'rect') {
        const rawSize = asArray(raw.size);
        result.size = [
            clamp(integer(rawSize[0], 1), 1, world.width - result.pos[0]),
            clamp(integer(rawSize[1], 1), 1, world.height - y),
            clamp(integer(rawSize[2], 1), 1, world.depth - result.pos[2]),
        ];
    } else if (shape === 'circle') {
        const edge = Math.max(1, Math.min(result.pos[0], world.width - 1 - result.pos[0], result.pos[2], world.depth - 1 - result.pos[2]));
        result.radius = clamp(integer(raw.radius, 1), 1, edge);
        result.height = clamp(integer(raw.height, 1), 1, world.height - y);
    } else {
        result.radiusX = clamp(integer(raw.radiusX, 1), 1, Math.max(1, Math.min(result.pos[0], world.width - 1 - result.pos[0])));
        result.radiusZ = clamp(integer(raw.radiusZ, 1), 1, Math.max(1, Math.min(result.pos[2], world.depth - 1 - result.pos[2])));
        result.height = clamp(integer(raw.height, 1), 1, world.height - y);
    }
    if (['absolute', 'terrainSurface', 'terrain_surface'].includes(raw.heightMode)) result.heightMode = raw.heightMode;
    if (Number.isFinite(Number(raw.scatterProbability))) result.scatterProbability = clamp(Number(raw.scatterProbability), 0, 1);
    for (const key of ['replaceOnly', 'requiresBelow']) {
        const values = asArray(raw[key]).map((value) => safeBlock(value, null)).filter(Boolean);
        if (values.length) result[key] = [...new Set(values)];
    }
    const inner = safeBlock(raw.innerBlockType, null);
    if (inner) {
        result.innerBlockType = inner;
        result.shellThickness = clamp(integer(raw.shellThickness, 1), 1, 32);
    }
    return result;
}

const GENERATED_LIQUID_FILL = /__(?:liquid|shore)_/;
const GENERATED_HABITABLE_FILL = /_(?:habitable_ground|spawn_safe_ground)$/;

function fillTopExclusive(fill) {
    const height = fill.shape === 'rect' ? fill.size?.[1] : fill.height;
    return Math.floor(Number(fill.pos?.[1]) || 0) + Math.max(1, Math.floor(Number(height) || 1));
}

function fillMinSpan(fill) {
    if (fill.shape === 'circle') return Math.max(1, fill.radius * 2);
    if (fill.shape === 'ellipse') return Math.max(1, Math.min(fill.radiusX, fill.radiusZ) * 2);
    return Math.max(1, Math.min(fill.size?.[0] ?? 1, fill.size?.[2] ?? 1));
}

function setFillVertical(fill, startY, topExclusive) {
    const result = copy(fill);
    const start = Math.max(1, Math.floor(startY));
    const top = Math.max(start + 1, Math.floor(topExclusive));
    result.pos[1] = start;
    if (result.shape === 'rect') result.size[1] = top - start;
    else result.height = top - start;
    return result;
}

function expandFill(fill, amount, world) {
    const expand = Math.max(0, Math.floor(amount));
    const result = copy(fill);
    if (result.shape === 'circle') {
        const edge = Math.min(result.pos[0], world.width - 1 - result.pos[0], result.pos[2], world.depth - 1 - result.pos[2]);
        result.radius = Math.max(1, Math.min(edge, result.radius + expand));
        return result;
    }
    if (result.shape === 'ellipse') {
        result.radiusX = Math.max(1, Math.min(result.pos[0], world.width - 1 - result.pos[0], result.radiusX + expand));
        result.radiusZ = Math.max(1, Math.min(result.pos[2], world.depth - 1 - result.pos[2], result.radiusZ + expand));
        return result;
    }
    const minX = Math.max(0, result.pos[0] - expand);
    const minZ = Math.max(0, result.pos[2] - expand);
    const maxX = Math.min(world.width, result.pos[0] + result.size[0] + expand);
    const maxZ = Math.min(world.depth, result.pos[2] + result.size[2] + expand);
    result.pos[0] = minX;
    result.pos[2] = minZ;
    result.size[0] = maxX - minX;
    result.size[2] = maxZ - minZ;
    return result;
}

/**
 * 液体fillを岩盤直上Y=1から水面まで切れ目のない1本の列へ展開する。
 * 浅瀬や斜面は水を途中で切ってAIRを残すのではなく、後段の砂・石の海底を
 * 持ち上げて表現する。sea_worldでは低い旧島も水面Yまで持ち上げる。
 */
export function applyLiquidDepthProfiles({ foundationFills, detailFills, generator, templateId, engine }) {
    const cfg = engine.liquidDepth;
    if (!cfg) return { foundationFills, detailFills, surfaceY: null };
    const liquids = new Set(asArray(cfg.liquidBlocks));
    // 自然水域はY=0の岩盤を保持し、その直上Y=1から必ず液体にする。
    // 設定値が壊れていても、この自然構造の不変条件は緩めない。
    const bottomY = 1;
    const naturalSubstrates = asArray(cfg.naturalSubstrateBlocks);
    const excludedLand = new Set(['AIR', 'BEDROCK', 'TRANSPARENT_BLOCK', 'SPACE_BLOCK', ...liquids]);
    const allOriginal = [...foundationFills, ...detailFills].filter((fill) => !GENERATED_LIQUID_FILL.test(fill.id));
    if (!allOriginal.some((fill) => liquids.has(fill.blockType))) {
        return { foundationFills: allOriginal.filter((fill) => foundationFills.includes(fill)), detailFills: allOriginal.filter((fill) => detailFills.includes(fill)), surfaceY: null };
    }

    const solidTops = foundationFills
        .filter((fill) => !excludedLand.has(fill.blockType) && !GENERATED_LIQUID_FILL.test(fill.id))
        .map(fillTopExclusive);
    const surfaceY = generator === 'void'
        ? Math.min(engine.world.height - 1, Math.max(
            templateId === 'sea_world' ? cfg.seaSurfaceY : cfg.voidMinSurfaceY,
            ...solidTops,
        ))
        : null;
    let generated = 0;
    const canGenerate = () => generated < cfg.maxGeneratedFills;

    const liquidColumn = (fill, forcedTop = null, { carveNaturalSubstrate = false } = {}) => {
        const top = Math.max(2, Math.floor(forcedTop ?? fillTopExclusive(fill)));
        const column = setFillVertical(fill, bottomY, top);
        column.id = `${fill.id}__liquid_column`;
        column.purpose = `${fill.purpose || fill.id}（岩盤直上Y=${bottomY}から水面Y=${top - 1}まで空洞なく連続する液体）`;
        // foundationの海は後段の島・建物土台を水へ戻さない。detailの湖・川等は
        // 自然地盤だけを掘り抜き、人工物とY=0の岩盤は保持する。
        column.replaceOnly = [...new Set([
            ...asArray(column.replaceOnly),
            'AIR',
            ...liquids,
            ...(carveNaturalSubstrate ? naturalSubstrates : []),
        ])];
        delete column.requiresBelow;
        generated++;
        return column;
    };

    const raiseFoundationLand = (fill) => {
        if (surfaceY == null || excludedLand.has(fill.blockType)) return copy(fill);
        const top = fillTopExclusive(fill);
        return top < surfaceY ? setFillVertical(fill, fill.pos[1], surfaceY) : copy(fill);
    };

    const nextFoundation = [];
    for (const original of foundationFills.filter((fill) => !GENERATED_LIQUID_FILL.test(fill.id))) {
        if (liquids.has(original.blockType)) {
            nextFoundation.push(liquidColumn(original, surfaceY));
            continue;
        }
        const land = raiseFoundationLand(original);
        if (templateId === 'sea_world' && !excludedLand.has(land.blockType)
            && fillMinSpan(land) < Math.min(engine.world.width, engine.world.depth) * 0.9) {
            for (const shelf of asArray(cfg.seaShelves)) {
                if (!canGenerate()) break;
                const expanded = expandFill(land, shelf.expand, engine.world);
                const shelfTop = Math.max(2, surfaceY - Math.max(1, shelf.waterDepth));
                const shaped = setFillVertical(expanded, 1, shelfTop);
                shaped.id = `${land.id}__shore_${toSnakeId(shelf.id, `shelf_${generated + 1}`)}`;
                shaped.purpose = `${land.purpose || land.id}の海底棚（水深${shelf.waterDepth}）`;
                shaped.blockType = shelf.blockType;
                shaped.blockTypes = undefined;
                shaped.replaceOnly = ['AIR', ...liquids];
                nextFoundation.push(shaped);
                generated++;
            }
        }
        nextFoundation.push(land);
    }

    const nextDetail = [];
    for (const fill of detailFills.filter((entry) => !GENERATED_LIQUID_FILL.test(entry.id))) {
        if (liquids.has(fill.blockType) && fill.heightMode !== 'terrainSurface' && fill.heightMode !== 'terrain_surface') {
            nextDetail.push(liquidColumn(fill, surfaceY, { carveNaturalSubstrate: true }));
        } else {
            nextDetail.push(copy(fill));
        }
    }
    return { foundationFills: nextFoundation, detailFills: nextDetail, surfaceY };
}

/**
 * 同一フォーマットの既存設計にも適用できる、無償・決定的な地表整合修復。
 * - v2.3 初期実装の液体レイヤーをY=1から水面まで延長し、水深部のAIR空洞を除去する
 * - foundationの海は固体を保護し、detailの湖・川等は自然地盤だけを掘り抜く
 * - スポーンを同じ開始地域の歩行可能面へ寄せ、スポーン／NPC の Y アンカーを足元高へ揃える
 *
 * @returns {boolean} 設計を変更した場合 true
 */
export function repairWorldDesignSurfaceAnchors(design) {
    if (!design || typeof design !== 'object') return false;
    const engine = designConfig().engine;
    const liquids = new Set(asArray(engine.liquidDepth?.liquidBlocks));
    const safeLiquidReplaceOnly = ['AIR', ...liquids];
    const naturalSubstrates = asArray(engine.liquidDepth?.naturalSubstrateBlocks);
    let changed = false;

    const repairLiquidFill = (fill, carveNaturalSubstrate) => {
        if (!fill || !GENERATED_LIQUID_FILL.test(str(fill.id)) || !liquids.has(fill.blockType)) return;
        const top = fillTopExclusive(fill);
        if (Number(fill.pos?.[1]) !== 1) {
            Object.assign(fill, setFillVertical(fill, 1, top));
            changed = true;
        }
        const requiredReplaceOnly = [
            ...safeLiquidReplaceOnly,
            ...(carveNaturalSubstrate ? naturalSubstrates : []),
        ];
        const existing = asArray(fill.replaceOnly);
        if (requiredReplaceOnly.some((block) => !existing.includes(block))) {
            fill.replaceOnly = [...new Set([...existing, ...requiredReplaceOnly])];
            changed = true;
        }
        if (fill.requiresBelow !== undefined) {
            delete fill.requiresBelow;
            changed = true;
        }
    };
    for (const fill of asArray(design.foundation?.fills)) repairLiquidFill(fill, false);
    for (const fill of asArray(design.detailFills)) repairLiquidFill(fill, true);

    const snapAnchorToWalkableSurface = (position) => {
        if (!Array.isArray(position) || position.length < 3) return;
        const info = designGroundInfo(design, position[0], position[2]);
        if (!isWalkableSurface(info, engine.placement.unwalkableSurfaceBlocks)) return;
        const footY = clamp(Math.floor(info.y) + 1, 1, engine.world.height - 1);
        if (position[1] === footY) return;
        position[1] = footY;
        changed = true;
    };

    if (Array.isArray(design.spawn) && design.spawn.length >= 3) {
        const currentInfo = designGroundInfo(design, design.spawn[0], design.spawn[2]);
        if (!isWalkableSurface(currentInfo, engine.placement.unwalkableSurfaceBlocks)) {
            const containingArea = asArray(design.areas).find((area) => area?.bounds && contains(area.bounds, design.spawn[0], design.spawn[2]));
            const primaryArea = containingArea
                ?? asArray(design.areas).find((area) => !['wilderness', 'sea', 'forest', 'desert', 'mountain'].includes(area.type))
                ?? asArray(design.areas)[0];
            if (primaryArea?.bounds) {
                const point = findWalkablePoint({
                    bounds: primaryArea.bounds,
                    preferred: { x: design.spawn[0], z: design.spawn[2] },
                    groundAt: (x, z) => designGroundInfo(design, x, z),
                    unwalkable: engine.placement.unwalkableSurfaceBlocks,
                    seedText: `repair_spawn_${design.mapId ?? primaryArea.id}`,
                });
                if (point && (design.spawn[0] !== point.x || design.spawn[2] !== point.z)) {
                    design.spawn[0] = point.x;
                    design.spawn[2] = point.z;
                    changed = true;
                }
            }
        }
    }
    snapAnchorToWalkableSurface(design.spawn);
    for (const placement of asArray(design.npcPlacements)) {
        snapAnchorToWalkableSurface(placement?.position);
    }
    return changed;
}

function normalizeTerrain(raw, engine, template, generator) {
    const defaults = engine.defaults;
    const elevation = Object.fromEntries(Object.entries(defaults.elevation).map(([key, fallback]) => [
        key,
        clamp(integer(raw?.elevation?.[key], fallback), 0, engine.world.height - 1),
    ]));
    // 段丘境界（Z 3分割）: エンジン getTerrainHeight の帯を設計から制御する
    const boundaries = {
        lowerUpperDivide: clamp(integer(raw?.boundaries?.lowerUpperDivide, defaults.boundaries.lowerUpperDivide), 0, engine.world.depth),
        castleUpperDivide: clamp(integer(raw?.boundaries?.castleUpperDivide, defaults.boundaries.castleUpperDivide), 0, engine.world.depth),
        cityRadius: clamp(integer(raw?.boundaries?.cityRadius, defaults.boundaries.cityRadius), 1, engine.world.depth),
    };
    if (boundaries.castleUpperDivide > boundaries.lowerUpperDivide) boundaries.castleUpperDivide = boundaries.lowerUpperDivide;
    // 段差をつなぐ大階段（エンジンは最大2件）。段差があるのに未設計なら既定テンプレートから自動生成し、
    // 平坦な設計では stairs=[] を明示してエンジンのフォールバック階段を無効化する
    let stairs = asArray(raw?.stairs).slice(0, 2).map((entry, index) => {
        const z = asArray(entry?.zRange);
        const z0 = clamp(integer(z[0], 0), 0, engine.world.depth - 1);
        const result = {
            id: toSnakeId(entry?.id, index === 0 ? 'stairs_lower_upper' : 'stairs_upper_castle'),
            zRange: [z0, clamp(integer(z[1], z0 + 1), z0 + 1, engine.world.depth)],
            steps: clamp(integer(entry?.steps, 5), 1, 40),
            depth: clamp(integer(entry?.depth, 12), 1, 64),
            widthX: clamp(integer(entry?.widthX, engine.world.width), 1, engine.world.width),
        };
        if (Number.isFinite(Number(entry?.fromHeight))) result.fromHeight = clamp(integer(entry.fromHeight, 0), 0, engine.world.height - 1);
        if (Number.isFinite(Number(entry?.toHeight))) result.toHeight = clamp(integer(entry.toHeight, 0), 0, engine.world.height - 1);
        return result;
    });
    if (stairs.length === 0) {
        const stepNeeded = {
            lowerUpperDivide: elevation.lowerCityHeight !== elevation.upperCityHeight,
            castleUpperDivide: elevation.upperCityHeight !== elevation.castleBaseHeight,
        };
        stairs = asArray(defaults.stairsTemplates)
            .filter((tpl) => stepNeeded[tpl.boundary])
            .map((tpl) => ({
                id: tpl.id,
                zRange: [
                    clamp(boundaries[tpl.boundary] - tpl.spanBefore, 0, engine.world.depth - 1),
                    clamp(boundaries[tpl.boundary] + tpl.spanAfter, 1, engine.world.depth),
                ],
                steps: tpl.steps,
                depth: tpl.depth,
                widthX: engine.world.width,
            }));
    }
    const noise = {
        scale: clamp(finite(raw?.noise?.scale, defaults.noise.scale), 0, 1),
        amplitude: clamp(finite(raw?.noise?.amplitude, defaults.noise.amplitude), 0, engine.world.height - 1),
    };
    const point = (value) => {
        const p = asArray(value);
        return [
            clamp(integer(p[0], engine.world.center.x), 0, engine.world.width - 1),
            clamp(integer(p[1], engine.world.center.z), 0, engine.world.depth - 1),
        ];
    };
    let rivers = asArray(raw?.water?.rivers).slice(0, 8).map((entry, index) => {
        const x = asArray(entry?.xRange);
        const z = asArray(entry?.zRange);
        const x0 = clamp(integer(x[0], 0), 0, engine.world.width - 1);
        const z0 = clamp(integer(z[0], 0), 0, engine.world.depth - 1);
        return {
            id: toSnakeId(entry?.id, `water_${index + 1}`),
            description: str(entry?.description),
            xRange: [x0, clamp(integer(x[1], engine.world.width), x0 + 1, engine.world.width)],
            zRange: [z0, clamp(integer(z[1], engine.world.depth), z0 + 1, engine.world.depth)],
            depth: clamp(integer(entry?.depth, 5), 2, Math.min(16, engine.world.height - 1)),
        };
    });
    const canals = asArray(raw?.water?.canals).slice(0, 16).map((entry, index) => ({
        id: toSnakeId(entry?.id, `canal_${index + 1}`),
        description: str(entry?.description),
        path: asArray(entry?.path).slice(0, 24).map(point),
        waterHalfWidth: clamp(integer(entry?.waterHalfWidth, 2), 1, 40),
        bankHalfWidth: clamp(integer(entry?.bankHalfWidth, 4), 1, 48),
        depth: clamp(integer(entry?.depth, 4), 2, Math.min(12, engine.world.height - 1)),
    })).filter((entry) => entry.path.length >= 2).map((entry) => ({
        ...entry,
        bankHalfWidth: Math.max(entry.bankHalfWidth, entry.waterHalfWidth),
    }));
    const ringCanals = asArray(raw?.water?.ringCanals).slice(0, 4).map((entry, index) => ({
        id: toSnakeId(entry?.id, `ring_canal_${index + 1}`),
        description: str(entry?.description),
        centerRelativeTo: entry?.centerRelativeTo === 'castle' ? 'castle' : 'world_center',
        radius: clamp(integer(entry?.radius, 50), 2, Math.floor(Math.min(engine.world.width, engine.world.depth) / 2) - 1),
        halfWidth: clamp(integer(entry?.halfWidth, 2), 1, 40),
        bankHalfWidth: clamp(integer(entry?.bankHalfWidth, 4), 1, 48),
        depth: clamp(integer(entry?.depth, 4), 2, Math.min(12, engine.world.height - 1)),
    })).map((entry) => ({ ...entry, bankHalfWidth: Math.max(entry.bankHalfWidth, entry.halfWidth) }));
    // エンジンの rivers は先頭1件のみ有効。overworld では 2 件目以降を等価な canal（直線水路）へ
    // 自動変換して設計を無言で失わない（void の水域は fills が担うため変換しない）
    if (generator === 'overworld' && rivers.length > 1) {
        for (const river of rivers.slice(1)) {
            if (canals.length >= 16) break;
            const xSpan = river.xRange[1] - river.xRange[0];
            const zSpan = river.zRange[1] - river.zRange[0];
            const alongX = xSpan >= zSpan;
            const waterHalfWidth = clamp(Math.floor((alongX ? zSpan : xSpan) / 2), 1, 40);
            const centerX = Math.floor((river.xRange[0] + river.xRange[1]) / 2);
            const centerZ = Math.floor((river.zRange[0] + river.zRange[1]) / 2);
            canals.push({
                id: `${river.id}_canal`,
                description: river.description || '2件目以降の川の自動変換（エンジンのriversは先頭1件のみ）',
                path: alongX ? [[river.xRange[0], centerZ], [river.xRange[1], centerZ]] : [[centerX, river.zRange[0]], [centerX, river.zRange[1]]],
                waterHalfWidth,
                bankHalfWidth: clamp(waterHalfWidth + 2, waterHalfWidth, 48),
                depth: river.depth,
            });
        }
        rivers = rivers.slice(0, 1);
    }
    const roads = asArray(raw?.roads).slice(0, 32).map((entry, index) => {
        const axis = entry?.axis === 'z' ? 'z' : 'x';
        const alongMax = axis === 'z' ? engine.world.width : engine.world.depth;
        const road = {
            id: toSnakeId(entry?.id, `road_${index + 1}`),
            description: str(entry?.description),
            type: entry?.type === 'line' ? 'line' : 'line',
            axis,
            position: clamp(integer(entry?.position, engine.world.center.x), 0, engine.world.width - 1),
            width: clamp(integer(entry?.width, 5), 1, 64),
            blockType: new Set(engine.safeBlocks).has(str(entry?.blockType)) ? str(entry.blockType) : 'COBBLESTONE',
        };
        // range=[軸方向の開始, 終了] で道路の長さを制限する（省略時は世界全域を貫く幹線）。
        // 範囲付き道路は⑤で地表追従の敷石 fill へ変換され、海・森まで延びない
        const rawRange = asArray(entry?.range);
        if (rawRange.length === 2) {
            const start = clamp(integer(rawRange[0], 0), 0, alongMax - 1);
            road.range = [start, clamp(integer(rawRange[1], alongMax), start + 1, alongMax)];
        }
        return road;
    });
    const rawVegetation = raw?.vegetation ?? {};
    const vegetation = {
        treeProbability: clamp(finite(rawVegetation.treeProbability, defaults.vegetation.treeProbability), 0, 1),
        treeEdgeMargin: clamp(integer(rawVegetation.treeEdgeMargin, defaults.vegetation.treeEdgeMargin), 0, Math.floor(engine.world.designWidth / 2)),
        treeScatterSpacing: clamp(integer(rawVegetation.treeScatterSpacing, defaults.vegetation.treeScatterSpacing), 1, 64),
        treeProfile: {
            trunkBase: clamp(integer(rawVegetation.treeProfile?.trunkBase, defaults.vegetation.treeProfile.trunkBase), 1, 20),
            canopy: asArray(rawVegetation.treeProfile?.canopy).length
                ? asArray(rawVegetation.treeProfile.canopy).slice(0, 20).map((value) => clamp(integer(value, 0), 0, 12))
                : copy(defaults.vegetation.treeProfile.canopy),
        },
        forestZones: asArray(rawVegetation.forestZones).slice(0, 8).map((entry, index) => ({
            id: toSnakeId(entry?.id, `forest_${index + 1}`),
            description: str(entry?.description),
            center: point(entry?.center),
            radius: clamp(integer(entry?.radius, 80), 1, Math.floor(engine.world.designWidth / 2)),
            surfaceBlock: new Set(engine.safeBlocks).has(str(entry?.surfaceBlock)) ? str(entry.surfaceBlock) : 'GRASS',
            treeProbability: clamp(finite(entry?.treeProbability, 0.03), 0, 1),
            treeScatterSpacing: clamp(integer(entry?.treeScatterSpacing, 5), 1, 64),
            treeProfile: entry?.treeProfile ? copy(entry.treeProfile) : copy(defaults.vegetation.treeProfile),
        })),
        flowerZones: asArray(rawVegetation.flowerZones).slice(0, 16).map((entry, index) => {
            const zone = {
                id: toSnakeId(entry?.id, `flowers_${index + 1}`),
                description: str(entry?.description),
                shape: entry?.shape === 'rect' ? 'rect' : 'circle',
                density: clamp(finite(entry?.density ?? entry?.flowerProbability, 0.15), 0, 1),
                types: asArray(entry?.types ?? entry?.flowerTypes).filter((value) => /^FLOWER_\d+$/.test(str(value))).slice(0, 10),
                scatterSpacing: clamp(integer(entry?.scatterSpacing, 3), 1, 32),
            };
            if (zone.types.length === 0) zone.types = ['FLOWER_01'];
            if (zone.shape === 'rect') zone.bounds = normalizeBounds(entry?.bounds, centeredBounds(engine.world, defaults), engine.world);
            else {
                const center = Array.isArray(entry?.center) ? point(entry.center) : point([entry?.center?.x, entry?.center?.z]);
                zone.center = { x: center[0], z: center[1] };
                zone.radius = clamp(integer(entry?.radius, 40), 1, Math.floor(engine.world.designWidth / 2));
            }
            return zone;
        }),
    };
    const allowedWeather = new Set(['clear', 'cloudy', 'rain', 'snow', 'storm', 'random', 'star']);
    const weatherType = template.forcedWeather
        ?? (allowedWeather.has(str(raw?.weather?.type)) ? str(raw.weather.type) : (template.defaultWeather ?? defaults.weather.type));
    return {
        surface: new Set(engine.safeBlocks).has(str(raw?.surface)) ? str(raw.surface) : template.habitableSurfaceBlock,
        features: asArray(raw?.features).map(str).filter(Boolean).slice(0, 16),
        elevation,
        boundaries,
        stairs,
        noise,
        water: { rivers, canals, ringCanals },
        roads,
        vegetation,
        weather: { type: weatherType, intensity: clamp(finite(raw?.weather?.intensity, defaults.weather.intensity), 0, 1) },
    };
}

/** groves（木立）の正規化。⑤で樹木 blockFill 群へ決定的展開される。 */
function normalizeGroves(raw, engine, fallbackBounds) {
    const world = engine.world;
    const groveCfg = engine.grove;
    return asArray(raw).slice(0, engine.limits.groves).map((entry, index) => {
        const shape = entry?.shape === 'rect' ? 'rect' : 'circle';
        const grove = {
            id: toSnakeId(entry?.id, `grove_${index + 1}`),
            purpose: str(entry?.purpose),
            shape,
            treeCount: clamp(integer(entry?.treeCount, groveCfg.defaultTreeCount), 1, groveCfg.maxTreesPerGrove),
            trunkHeight: clamp(integer(entry?.trunkHeight, groveCfg.tree.trunkHeight), 2, 8),
        };
        if (shape === 'rect') {
            grove.bounds = normalizeBounds(entry?.bounds, fallbackBounds, world);
        } else {
            const rawCenter = Array.isArray(entry?.center) ? { x: entry.center[0], z: entry.center[1] } : (entry?.center ?? {});
            grove.center = {
                x: clamp(integer(rawCenter.x, world.center.x), 0, world.width - 1),
                z: clamp(integer(rawCenter.z, world.center.z), 0, world.depth - 1),
            };
            grove.radius = clamp(integer(entry?.radius, 40), 2, Math.floor(world.designWidth / 2));
        }
        return grove;
    });
}

/**
 * LLM 出力を検証・正規化し、⑤で機械適用できる実寸設計へ確定する。
 * @param {object} options { blueprints?: projectBlueprintCatalog() の結果（kind=blueprint の検証に使用） }
 */
export function normalizeWorldDesign(raw, analysis, options = {}) {
    const cfg = designConfig();
    const engine = cfg.engine;
    const world = engine.world;
    const defaults = engine.defaults;
    const limits = engine.limits;
    const blueprints = asArray(options.blueprints);
    const autoNotes = [];
    const startMapId = analysis.game.startMapId;
    const language = analysis.game.generationLanguage === 'en' ? 'en' : 'ja';
    const templates = templateCatalog();
    const templateIds = new Set(templates.map((entry) => entry.id));
    const selectedTemplateId = templateIds.has(str(raw?.foundation?.baseTemplateId))
        ? str(raw.foundation.baseTemplateId)
        : analysis.game.startWorldBase;
    const template = templates.find((entry) => entry.id === selectedTemplateId) ?? templates[0];
    const generator = ['overworld', 'void'].includes(raw?.foundation?.generator)
        ? raw.foundation.generator
        : template.defaultGenerator;
    const mode = ['inherit', 'adapt', 'custom'].includes(raw?.foundation?.mode) ? raw.foundation.mode : 'adapt';

    const sourceAreas = (asArray(raw?.areas).length ? raw.areas : asArray(analysis.world.startMap.areas))
        // v1 正規化器が物語と無関係に自動追加していた合成エリア。v2 移行時だけ除去し、
        // 作品由来の正規エリアや v2 系で利用者が明示した同名IDは保持する。
        .filter((area) => String(raw?.formatVersion ?? '').startsWith('2.') || !(area?.id === 'outer_field' && area?.nameJa === '外周の原野'));
    const fallbackBounds = centeredBounds(world, defaults);
    const allowedAreaTypes = new Set(engine.areaTypes);
    const safeBlocks = new Set(engine.safeBlocks);
    const walkableAreaTypes = new Set(asArray(engine.placement.walkableAreaTypes));
    const unwalkableSurfaceBlocks = new Set(asArray(engine.placement.unwalkableSurfaceBlocks));
    const areas = sourceAreas.slice(0, limits.areas).map((area, index) => {
        const bounds = normalizeBounds(area?.bounds, fallbackBounds, world);
        const type = allowedAreaTypes.has(area?.type) ? area.type : (index === 0 ? 'settlement' : 'special');
        const rawNameJa = str(area?.nameJa);
        const rawNameEn = str(area?.nameEn);
        const genericName = language === 'en' ? `Area ${index + 1}` : `エリア${index + 1}`;
        const primaryName = language === 'en'
            ? (rawNameEn || str(area?.name) || rawNameJa || genericName)
            : (rawNameJa || str(area?.name) || rawNameEn || genericName);
        const requestedSurface = safeBlocks.has(str(area?.surfaceBlock))
            ? str(area.surfaceBlock)
            : (safeBlocks.has(str(raw?.terrain?.surface)) ? str(raw.terrain.surface) : template.habitableSurfaceBlock);
        const surfaceBlock = walkableAreaTypes.has(type) && unwalkableSurfaceBlocks.has(requestedSurface)
            ? template.habitableSurfaceBlock
            : requestedSurface;
        if (surfaceBlock !== requestedSurface) {
            autoNotes.push(`${str(area?.nameJa) || str(area?.name) || `エリア${index + 1}`}: 歩行エリアの表面 ${requestedSurface} を非水地面 ${surfaceBlock} へ補正`);
        }
        return {
            id: toSnakeId(area?.id ?? area?.nameEn ?? area?.nameJa ?? area?.name, `area_${index + 1}`),
            nameJa: rawNameJa || primaryName,
            nameEn: rawNameEn || primaryName,
            type,
            purpose: str(area?.purpose),
            bounds,
            boundsHint: `${bounds.maxX - bounds.minX}×${bounds.maxZ - bounds.minZ} blocks (${bounds.minX},${bounds.minZ})–(${bounds.maxX},${bounds.maxZ})`,
            surfaceBlock,
            priority: clamp(integer(area?.priority, index === 0 ? defaults.primaryPriority : defaults.primaryPriority + index), 1, 100),
        };
    });
    if (areas.length === 0) {
        areas.push({
            id: startMapId,
            nameJa: localizedForGeneration(analysis.world.startMap.name, language, startMapId),
            nameEn: localizedForGeneration(analysis.world.startMap.name, language, startMapId),
            type: 'settlement',
            purpose: analysis.world.startMap.concept,
            bounds: fallbackBounds,
            boundsHint: `${fallbackBounds.maxX - fallbackBounds.minX}×${fallbackBounds.maxZ - fallbackBounds.minZ} blocks`,
            surfaceBlock: template.habitableSurfaceBlock,
            priority: defaults.primaryPriority,
        });
    }
    const hasWorldBackground = areas.some((area) => area.bounds.minX === 0 && area.bounds.maxX === world.width
        && area.bounds.minZ === 0 && area.bounds.maxZ === world.depth);
    if (!hasWorldBackground && areas.length < limits.areas) {
        const backgroundType = ({ sea_world: 'sea', water_world: 'sea', soil_world: 'desert', sky_world: 'special', space_world: 'special' })[template.id] ?? 'wilderness';
        const backgroundSurface = ({ sea_world: 'WATER', water_world: 'WATER', soil_world: 'SAND', sky_world: 'TRANSPARENT_BLOCK', space_world: 'SPACE_BLOCK' })[template.id] ?? 'GRASS';
        areas.push({
            id: `${startMapId}_environment`,
            nameJa: template.label,
            nameEn: template.id,
            type: backgroundType,
            purpose: `${template.hint}としてワールド全域の背景を構成する`,
            bounds: { minX: 0, maxX: world.width, minZ: 0, maxZ: world.depth },
            boundsHint: `${world.width}×${world.depth} blocks（全域）`,
            surfaceBlock: backgroundSurface,
            priority: defaults.backgroundPriority,
        });
    }
    const areaIds = new Set(areas.map((area) => area.id));
    const primaryArea = areas.find((area) => !['wilderness', 'sea', 'forest', 'desert', 'mountain'].includes(area.type)) ?? areas[0];

    const terrain = normalizeTerrain(raw?.terrain ?? {}, engine, template, generator);
    const inheritBaseFills = generator === 'overworld'
        ? false
        : (typeof raw?.foundation?.inheritBaseFills === 'boolean' ? raw.foundation.inheritBaseFills : mode !== 'custom');
    const inherited = inheritBaseFills
        ? asArray(template.foundationFills).map((entry, index) => normalizeFill(entry, index, 'base', engine, 'BEDROCK')).filter(Boolean)
        : [];
    const inheritedIds = new Set(inherited.map((fill) => fill.id));
    const customFoundation = asArray(raw?.foundation?.fills)
        .filter((entry) => !GENERATED_LIQUID_FILL.test(str(entry?.id))
            && !GENERATED_HABITABLE_FILL.test(str(entry?.id))
            && !inheritedIds.has(toSnakeId(entry?.id, '')))
        .slice(0, limits.foundationFills)
        .map((entry, index) => normalizeFill(entry, index, 'foundation', engine, terrain.surface)).filter(Boolean);
    const baseFoundation = [...inherited];
    if (generator === 'void' && baseFoundation.length === 0 && customFoundation.length === 0) {
        const fallbackBedrock = templates.find((entry) => entry.id === 'bottom_world')?.foundationFills?.[0];
        const fill = normalizeFill(fallbackBedrock, 0, 'base', engine, 'BEDROCK');
        if (fill) baseFoundation.push(fill);
    }
    const generatedHabitableGround = [];
    if (generator === 'void' && template.requiresHabitablePlatform) {
        for (const area of areas) {
            // 自動追加した全域背景（星空・雲海等）は足場へ変えない。
            if (area.id === `${startMapId}_environment` || !walkableAreaTypes.has(area.type)) continue;
            const fill = normalizeFill({
                id: `${area.id}_habitable_ground`,
                purpose: `${area.nameJa}の歩行可能な非水地盤（エリア全域の安全保証）`,
                shape: 'rect',
                blockType: area.surfaceBlock || template.habitableSurfaceBlock,
                pos: [area.bounds.minX, 1, area.bounds.minZ],
                size: [area.bounds.maxX - area.bounds.minX, 1, area.bounds.maxZ - area.bounds.minZ],
            }, generatedHabitableGround.length, 'foundation', engine, template.habitableSurfaceBlock);
            if (fill) generatedHabitableGround.push(fill);
        }
        if (generatedHabitableGround.length > 0) {
            autoNotes.push(`歩行エリア${generatedHabitableGround.length}件へ非水地盤を自動保証`);
        }
    }
    // 背景（海・透明床）→保証地盤→LLMの島・浜・地形の順。後段の固体意匠を保持しつつ、
    // foundation液体は固体を上書きしないため、全域海が末尾にあっても歩行地域は水没しない。
    const foundationFills = [...baseFoundation, ...generatedHabitableGround, ...customFoundation];

    const detailFills = asArray(raw?.detailFills).filter((entry) => !GENERATED_LIQUID_FILL.test(str(entry?.id))).slice(0, limits.detailFills)
        .map((entry, index) => normalizeFill(entry, index, 'detail', engine, terrain.surface)).filter(Boolean);
    const liquidProfiles = applyLiquidDepthProfiles({
        foundationFills, detailFills, generator, templateId: template.id, engine,
    });
    foundationFills.splice(0, foundationFills.length, ...liquidProfiles.foundationFills);
    detailFills.splice(0, detailFills.length, ...liquidProfiles.detailFills);
    const groves = normalizeGroves(raw?.groves, engine, fallbackBounds);
    // 森ゾーンのエンジン制約対応: overworld は主要1件のみ有効・void では未反映のため、
    // 反映されない森を groves（木立）へ自動変換して設計を無言で失わない
    const sortedForests = [...terrain.vegetation.forestZones].sort((a, b) => b.radius - a.radius);
    terrain.vegetation.forestZones = generator === 'overworld' ? sortedForests.slice(0, 1) : [];
    const groveCfg = engine.grove;
    for (const zone of (generator === 'overworld' ? sortedForests.slice(1) : sortedForests)) {
        if (groves.length >= limits.groves) break;
        const estimatedTrees = Math.round(Math.PI * zone.radius * zone.radius * zone.treeProbability);
        groves.push({
            id: toSnakeId(`${zone.id}_grove`, `grove_${groves.length + 1}`),
            purpose: zone.description || '森ゾーンの自動変換（エンジン未対応分を木立で表現）',
            shape: 'circle',
            center: { x: zone.center[0], z: zone.center[1] },
            radius: zone.radius,
            treeCount: clamp(estimatedTrees, 3, groveCfg.maxTreesPerGrove),
            trunkHeight: clamp(integer(zone.treeProfile?.trunkBase, groveCfg.tree.trunkHeight), 2, 8),
        });
    }

    const landmarks = normalizeLandmarks(raw?.landmarks, engine, world, fallbackBounds, blueprints, autoNotes);
    const cityWall = normalizeCityWall(raw?.cityWall, engine, generator, autoNotes);
    if (cityWall.gate.enabled) {
        // 外門はエンジン仕様上「全長の南北道路 main_road_ns」を参照する。無ければ最寄りの全長南北道路を
        // その id へ改名し、南北の全長道路が無い場合は門を諦めて記録する
        const fullNorthSouth = terrain.roads.filter((road) => road.axis === 'x' && !road.range);
        if (fullNorthSouth.length > 0) {
            const main = fullNorthSouth.find((road) => road.id === 'main_road_ns')
                ?? fullNorthSouth.sort((a, b) => Math.abs(a.position - world.center.x) - Math.abs(b.position - world.center.x))[0];
            main.id = 'main_road_ns';
        } else {
            cityWall.gate.enabled = false;
            autoNotes.push('cityWall.gate には全長の南北道路が必要（range 無し・axis=x）。門なしの城壁として生成する');
        }
    }

    const defaultY = generator === 'overworld' ? terrain.elevation.groundLevel + 1 : defaults.voidSpawnY;
    const rawSpawn = asArray(raw?.spawn);
    const primaryCenter = {
        x: Math.floor((primaryArea.bounds.minX + primaryArea.bounds.maxX) / 2),
        z: Math.floor((primaryArea.bounds.minZ + primaryArea.bounds.maxZ) / 2),
    };
    const requestedSpawn = {
        x: clamp(integer(rawSpawn[0], primaryCenter.x), 0, world.width - 1),
        z: clamp(integer(rawSpawn[2], primaryCenter.z), 0, world.depth - 1),
    };
    if (!contains(primaryArea.bounds, requestedSpawn.x, requestedSpawn.z)) {
        requestedSpawn.x = primaryCenter.x;
        requestedSpawn.z = primaryCenter.z;
    }
    const groundAt = generator === 'overworld'
        ? (x, z) => designGroundInfo({
            foundation: { generator },
            scale: { worldSize: world },
            terrain,
            areas,
            detailFills,
        }, x, z)
        : (x, z) => surfaceInfoFromFills([...foundationFills, ...detailFills], x, z, world.height);
    let resolvedSpawn = findWalkablePoint({
        bounds: primaryArea.bounds,
        preferred: requestedSpawn,
        groundAt,
        unwalkable: engine.placement.unwalkableSurfaceBlocks,
        seedText: `spawn_${startMapId}_${primaryArea.id}`,
    });

    // 歩行エリア全域を detail の液体が覆う異常設計でも、開始地点だけは水中へ落とさない。
    // 最終 detail として同じ水面高まで固体列を置き、液体を確実に上書きする。
    if (!resolvedSpawn && generator === 'void') {
        const current = groundAt(primaryCenter.x, primaryCenter.z);
        const topExclusive = clamp((current?.y ?? Math.max(1, defaultY - 1)) + 1, 2, world.height);
        const patchRadius = 2;
        const minX = clamp(primaryCenter.x - patchRadius, primaryArea.bounds.minX, primaryArea.bounds.maxX - 1);
        const minZ = clamp(primaryCenter.z - patchRadius, primaryArea.bounds.minZ, primaryArea.bounds.maxZ - 1);
        const maxX = clamp(primaryCenter.x + patchRadius + 1, minX + 1, primaryArea.bounds.maxX);
        const maxZ = clamp(primaryCenter.z + patchRadius + 1, minZ + 1, primaryArea.bounds.maxZ);
        const safeFill = normalizeFill({
            id: `${primaryArea.id}_spawn_safe_ground`,
            purpose: `${primaryArea.nameJa}の開始地点を水中にしない最終安全地盤`,
            shape: 'rect',
            blockType: primaryArea.surfaceBlock || template.habitableSurfaceBlock,
            pos: [minX, 1, minZ],
            size: [maxX - minX, topExclusive - 1, maxZ - minZ],
        }, detailFills.length, 'detail', engine, template.habitableSurfaceBlock);
        if (safeFill) detailFills.push(safeFill);
        resolvedSpawn = findWalkablePoint({
            bounds: primaryArea.bounds,
            preferred: primaryCenter,
            groundAt,
            unwalkable: engine.placement.unwalkableSurfaceBlocks,
            seedText: `spawn_safe_${startMapId}_${primaryArea.id}`,
        });
        autoNotes.push(`${primaryArea.nameJa}: 全面水域からの開始を防ぐ5×5以下の安全地盤を追加`);
    }
    const spawn = resolvedSpawn
        ? [
            resolvedSpawn.x,
            clamp(resolvedSpawn.info.y + 1, 1, world.height - 1),
            resolvedSpawn.z,
        ]
        : [
            primaryCenter.x,
            clamp(overworldBandHeight(terrain, primaryCenter.z) + 1, 1, world.height - 1),
            primaryCenter.z,
        ];

    const buildingCfg = engine.building;
    const safeBlockSet = new Set(engine.safeBlocks);
    // 素材・階高・屋根形式（現代高層は flatRoof）を作品に合わせて指定できる（省略時は既定材）
    const buildingLook = (entry) => {
        const look = {};
        for (const key of ['wallBlock', 'roofBlock', 'floorBlock', 'windowBlock']) {
            if (safeBlockSet.has(str(entry?.[key]))) look[key] = str(entry[key]);
        }
        if (Number.isFinite(Number(entry?.floorHeight))) look.floorHeight = clamp(Math.floor(Number(entry.floorHeight)), 3, 8);
        if (entry?.flatRoof === true) look.flatRoof = true;
        return look;
    };
    // 高層でも屋根までワールド高さへ収まる階数上限（floors×floorHeight+屋根余裕12）
    const fitFloors = (floorHeight) => Math.max(1, Math.floor((world.height - 12) / floorHeight));
    const perAreaCount = new Map();
    const buildings = asArray(raw?.buildings).slice(0, limits.buildings).map((building, index) => {
        const areaId = areaIds.has(str(building?.areaId)) ? str(building.areaId) : primaryArea.id;
        const area = areas.find((entry) => entry.id === areaId) ?? primaryArea;
        const look = buildingLook(building);
        const width = clamp(integer(building?.width, buildingCfg.defaultWidth), buildingCfg.minWidth, buildingCfg.maxWidth);
        const depth = clamp(integer(building?.depth, buildingCfg.defaultDepth), buildingCfg.minDepth, buildingCfg.maxDepth);
        const floors = clamp(
            integer(building?.floors, buildingCfg.defaultFloors),
            buildingCfg.minFloors,
            Math.min(buildingCfg.maxFloors, fitFloors(look.floorHeight ?? buildingCfg.defaultTypeSettings.floorHeight)),
        );
        const localIndex = perAreaCount.get(areaId) ?? 0;
        perAreaCount.set(areaId, localIndex + 1);
        const columns = Math.max(1, Math.floor((area.bounds.maxX - area.bounds.minX) / (width + buildingCfg.defaultSpacing)));
        const rawPosition = asArray(building?.position ?? building?.pos);
        const fallbackX = area.bounds.minX + buildingCfg.defaultSpacing + (localIndex % columns) * (width + buildingCfg.defaultSpacing);
        const fallbackZ = area.bounds.minZ + buildingCfg.defaultSpacing + Math.floor(localIndex / columns) * (depth + buildingCfg.defaultSpacing);
        const position = [
            clamp(integer(rawPosition[0], fallbackX), area.bounds.minX, Math.max(area.bounds.minX, area.bounds.maxX - width)),
            clamp(integer(rawPosition[1], defaultY), 0, world.height - 1),
            clamp(integer(rawPosition[2], fallbackZ), area.bounds.minZ, Math.max(area.bounds.minZ, area.bounds.maxZ - depth)),
        ];
        return {
            id: toSnakeId(building?.id ?? building?.nameEn ?? building?.nameJa, `building_${index + 1}`),
            nameJa: str(building?.nameJa) || str(building?.name) || `建物${index + 1}`,
            nameEn: str(building?.nameEn) || `Building ${index + 1}`,
            purpose: str(building?.purpose),
            position,
            width,
            depth,
            floors,
            ...look,
            sizeHint: `${width}×${depth}×${floors} floors`,
            areaId,
            style: str(building?.style),
            surroundings: asArray(building?.surroundings).map(str).filter(Boolean).slice(0, 8),
        };
    });

    const buildingClusters = asArray(raw?.buildingClusters).slice(0, limits.buildingClusters).map((cluster, index) => {
        const areaId = areaIds.has(str(cluster?.areaId)) ? str(cluster.areaId) : primaryArea.id;
        const area = areas.find((entry) => entry.id === areaId) ?? primaryArea;
        const bounds = normalizeBounds(cluster?.bounds, area.bounds, world);
        const spacing = clamp(integer(cluster?.spacing, buildingCfg.defaultSpacing), 1, 64);
        const buildingWidth = clamp(integer(cluster?.buildingWidth, buildingCfg.defaultWidth), 1, Math.min(buildingCfg.maxWidth, bounds.maxX - bounds.minX));
        const buildingDepth = clamp(integer(cluster?.buildingDepth, buildingCfg.defaultDepth), 1, Math.min(buildingCfg.maxDepth, bounds.maxZ - bounds.minZ));
        const columns = Math.max(1, Math.floor((bounds.maxX - bounds.minX + spacing) / (buildingWidth + spacing)));
        const rows = Math.max(1, Math.floor((bounds.maxZ - bounds.minZ + spacing) / (buildingDepth + spacing)));
        const look = buildingLook(cluster);
        const clusterMaxFloors = Math.min(buildingCfg.maxFloors, fitFloors(look.floorHeight ?? buildingCfg.defaultTypeSettings.floorHeight));
        return {
            id: toSnakeId(cluster?.id, `building_cluster_${index + 1}`),
            areaId,
            purpose: str(cluster?.purpose),
            bounds,
            count: clamp(integer(cluster?.count, 1), 1, Math.min(buildingCfg.maxClusterCount, columns * rows)),
            buildingWidth,
            buildingDepth,
            floorsMin: clamp(integer(cluster?.floorsMin, 1), buildingCfg.minFloors, clusterMaxFloors),
            floorsMax: clamp(integer(cluster?.floorsMax, buildingCfg.defaultFloors), buildingCfg.minFloors, clusterMaxFloors),
            ...look,
            spacing,
            style: str(cluster?.style),
            surroundings: asArray(cluster?.surroundings).map(str).filter(Boolean).slice(0, 8),
        };
    }).map((cluster) => ({ ...cluster, floorsMax: Math.max(cluster.floorsMax, cluster.floorsMin) }));

    const npcCastIds = new Set(analysis.cast.filter((cast) => cast.role === 'npc').map((cast) => cast.id));
    const enemyCastIds = new Set(analysis.cast.filter((cast) => cast.role === 'enemy').map((cast) => cast.id));
    const npcPlacements = asArray(raw?.npcPlacements).filter((entry) => npcCastIds.has(str(entry?.castId))).slice(0, limits.npcPlacements).map((entry) => {
        const areaId = areaIds.has(str(entry?.areaId)) ? str(entry.areaId) : primaryArea.id;
        const area = areas.find((candidate) => candidate.id === areaId) ?? primaryArea;
        const position = asArray(entry?.position);
        return {
            castId: str(entry.castId),
            areaId,
            position: [
                clamp(integer(position[0], Math.floor((area.bounds.minX + area.bounds.maxX) / 2)), area.bounds.minX, area.bounds.maxX - 1),
                clamp(integer(position[1], defaultY), 0, world.height - 1),
                clamp(integer(position[2], Math.floor((area.bounds.minZ + area.bounds.maxZ) / 2)), area.bounds.minZ, area.bounds.maxZ - 1),
            ],
            note: str(entry?.note),
        };
    });
    const enemyPlacements = asArray(raw?.enemyPlacements).filter((entry) => {
        const castId = str(entry?.castId);
        return !castId || enemyCastIds.has(castId);
    }).slice(0, limits.enemyPlacements).map((entry, index) => {
        const fallbackCast = analysis.cast.filter((cast) => cast.role === 'enemy')[index];
        const areaId = areaIds.has(str(entry?.areaId)) ? str(entry.areaId) : primaryArea.id;
        const area = areas.find((candidate) => candidate.id === areaId) ?? primaryArea;
        const requestedBounds = normalizeBounds(entry?.spawnBounds, area.bounds, world);
        const spawnBounds = {
            minX: clamp(requestedBounds.minX, area.bounds.minX, area.bounds.maxX - 1),
            maxX: clamp(requestedBounds.maxX, area.bounds.minX + 1, area.bounds.maxX),
            minZ: clamp(requestedBounds.minZ, area.bounds.minZ, area.bounds.maxZ - 1),
            maxZ: clamp(requestedBounds.maxZ, area.bounds.minZ + 1, area.bounds.maxZ),
        };
        if (spawnBounds.maxX <= spawnBounds.minX) spawnBounds.maxX = Math.min(area.bounds.maxX, spawnBounds.minX + 1);
        if (spawnBounds.maxZ <= spawnBounds.minZ) spawnBounds.maxZ = Math.min(area.bounds.maxZ, spawnBounds.minZ + 1);
        if (entry?.enabled === false) {
            autoNotes.push(`敵 ${str(entry?.castId) || fallbackCast?.id || ''}: enabled=false は廃止（解析済みの敵は必ず配置する）。設計の生息地で有効化した`);
        }
        const castId = enemyCastIds.has(str(entry?.castId)) ? str(entry.castId) : (fallbackCast?.id ?? '');
        const selectedCast = analysis.cast.find((cast) => cast.id === castId) ?? fallbackCast;
        const enemyRank = normalizeEnemyRank(selectedCast?.enemyRank ?? entry?.enemyRank);
        return {
            castId,
            name: str(entry?.name) || localizedForGeneration(selectedCast?.name, language, ''),
            enabled: true,
            enemyRank,
            displayScale: normalizeEnemyDisplayScale(enemyRank, entry?.displayScale),
            areaId,
            spawnBounds,
            habitatType: toSnakeId(entry?.habitatType, 'wilderness'),
            habitatRequirements: asArray(entry?.habitatRequirements).map(str).filter(Boolean).slice(0, 8),
            behavior: ['wander', 'stationary', 'patrol'].includes(entry?.behavior) ? entry.behavior : 'wander',
            groupSize: clamp(integer(entry?.groupSize, 1), 1, 16),
            activityRadius: clamp(integer(entry?.activityRadius, 12), 0, 100),
            note: str(entry?.note),
        };
    });
    // 解析で登録された非プレイアブル cast（NPC・敵）は必ずマップへ配置する（2026-07-13 ユーザー要件）。
    // LLM 設計から漏れた分を決定的に補完する。位置の質は LLM 設計が上のため、プロンプト側でも全員配置を指示済み
    const castPlacement = engine.placement.castPlacement;
    const groundProbe = (x, z) => {
        if (generator === 'overworld') return true;
        const info = surfaceInfoFromFills([...foundationFills, ...detailFills], x, z, world.height);
        return isWalkableSurface(info, engine.placement.unwalkableSurfaceBlocks);
    };
    const placedNpcIds = new Set(npcPlacements.map((entry) => entry.castId));
    for (const cast of analysis.cast.filter((entry) => entry.role === 'npc' && !placedNpcIds.has(entry.id))) {
        if (npcPlacements.length >= limits.npcPlacements) break;
        const spot = scatterPointsInShape({
            seedText: `npc_auto_${cast.id}`,
            count: 1,
            shape: { kind: 'rect', ...primaryArea.bounds },
            accept: groundProbe,
        })[0] ?? {
            x: Math.floor((primaryArea.bounds.minX + primaryArea.bounds.maxX) / 2),
            z: Math.floor((primaryArea.bounds.minZ + primaryArea.bounds.maxZ) / 2),
        };
        npcPlacements.push({
            castId: cast.id,
            areaId: primaryArea.id,
            position: [spot.x, defaultY, spot.z],
            note: '設計に無かったため自動配置（位置・会話は再設計で改善可能）',
        });
        autoNotes.push(`NPC ${cast.id}: 設計に無かったため ${primaryArea.nameJa} へ自動配置`);
    }
    const placedEnemyIds = new Set(enemyPlacements.map((entry) => entry.castId));
    for (const cast of analysis.cast.filter((entry) => entry.role === 'enemy' && !placedEnemyIds.has(entry.id))) {
        if (enemyPlacements.length >= limits.enemyPlacements) break;
        // 生息地らしいエリアを優先順で選ぶ（無ければ主要エリア）。同種タイプが複数あれば最大のもの
        const areaSize = (area) => (area.bounds.maxX - area.bounds.minX) * (area.bounds.maxZ - area.bounds.minZ);
        const habitatArea = castPlacement.enemyAreaTypePriority
            .map((type) => areas.filter((area) => area.type === type).sort((a, b) => areaSize(b) - areaSize(a))[0])
            .find(Boolean)
            ?? primaryArea;
        const span = Math.min(castPlacement.enemySpawnSpan, habitatArea.bounds.maxX - habitatArea.bounds.minX, habitatArea.bounds.maxZ - habitatArea.bounds.minZ);
        const center = scatterPointsInShape({
            seedText: `enemy_auto_${cast.id}`,
            count: 1,
            shape: {
                kind: 'rect',
                minX: habitatArea.bounds.minX + Math.floor(span / 2),
                maxX: Math.max(habitatArea.bounds.minX + Math.floor(span / 2) + 1, habitatArea.bounds.maxX - Math.floor(span / 2)),
                minZ: habitatArea.bounds.minZ + Math.floor(span / 2),
                maxZ: Math.max(habitatArea.bounds.minZ + Math.floor(span / 2) + 1, habitatArea.bounds.maxZ - Math.floor(span / 2)),
            },
        })[0] ?? {
            x: Math.floor((habitatArea.bounds.minX + habitatArea.bounds.maxX) / 2),
            z: Math.floor((habitatArea.bounds.minZ + habitatArea.bounds.maxZ) / 2),
        };
        enemyPlacements.push({
            castId: cast.id,
            name: localizedForGeneration(cast.name, language, cast.id),
            enabled: true,
            enemyRank: normalizeEnemyRank(cast.enemyRank),
            displayScale: normalizeEnemyDisplayScale(normalizeEnemyRank(cast.enemyRank), undefined),
            areaId: habitatArea.id,
            spawnBounds: {
                minX: clamp(center.x - Math.floor(span / 2), habitatArea.bounds.minX, habitatArea.bounds.maxX - 1),
                maxX: clamp(center.x + Math.ceil(span / 2), habitatArea.bounds.minX + 1, habitatArea.bounds.maxX),
                minZ: clamp(center.z - Math.floor(span / 2), habitatArea.bounds.minZ, habitatArea.bounds.maxZ - 1),
                maxZ: clamp(center.z + Math.ceil(span / 2), habitatArea.bounds.minZ + 1, habitatArea.bounds.maxZ),
            },
            habitatType: habitatArea.type,
            habitatRequirements: [],
            behavior: 'wander',
            groupSize: castPlacement.enemyGroupSize,
            activityRadius: castPlacement.enemyActivityRadius,
            note: '設計に無かったため自動配置（生息地の質は再設計で改善可能）',
        });
        autoNotes.push(`敵 ${cast.id}: 設計に無かったため ${habitatArea.nameJa} へ自動配置`);
    }
    const usedEventIds = new Set();
    const eventIdeas = asArray(raw?.eventIdeas).slice(0, limits.eventIdeas).map((event, index) => {
        let id = toSnakeId(event?.id ?? event?.summary, `event_${index + 1}`);
        let suffix = 2;
        while (usedEventIds.has(id)) id = `${id}_${suffix++}`;
        usedEventIds.add(id);
        const areaId = areaIds.has(str(event?.areaId)) ? str(event.areaId) : primaryArea.id;
        const area = areas.find((candidate) => candidate.id === areaId) ?? primaryArea;
        const requestedPosition = asArray(event?.position);
        const hasPosition = requestedPosition.length >= 3;
        const trigger = str(event?.trigger) === 'proximity' || hasPosition ? 'proximity' : 'area_enter';
        const summary = str(event?.summary);
        const message = str(event?.message) || summary;
        const lines = asArray(event?.lines).map(str).filter(Boolean).slice(0, 4);
        const presentation = event?.presentation === 'dialogue' ? 'dialogue' : 'notification';
        const position = trigger === 'proximity'
            ? [
                clamp(integer(requestedPosition[0], Math.floor((area.bounds.minX + area.bounds.maxX) / 2)), area.bounds.minX, area.bounds.maxX - 1),
                clamp(integer(requestedPosition[1], defaultY), 0, world.height - 1),
                clamp(integer(requestedPosition[2], Math.floor((area.bounds.minZ + area.bounds.maxZ) / 2)), area.bounds.minZ, area.bounds.maxZ - 1),
            ]
            : null;
        return {
            id,
            trigger,
            areaId,
            ...(position ? { position, radius: clamp(finite(event?.radius, 12), 2, 40) } : {}),
            presentation,
            message,
            ...(presentation === 'dialogue'
                ? { speaker: str(event?.speaker) || '???', lines: lines.length > 0 ? lines : [message].filter(Boolean) }
                : {}),
            once: event?.once !== false,
            storyBasis: str(event?.storyBasis),
            summary,
        };
    });
    const allowedWeather = new Set(['clear', 'cloudy', 'rain', 'snow', 'storm', 'random', 'star']);
    const environmentWeather = template.forcedWeather
        ?? (allowedWeather.has(str(raw?.environment?.weather?.type)) ? str(raw.environment.weather.type) : terrain.weather.type);
    const environment = {
        skyMood: str(raw?.environment?.skyMood) || template.sky,
        fogMood: str(raw?.environment?.fogMood),
        lightingMood: str(raw?.environment?.lightingMood),
        timeOfDay: {
            enabled: raw?.environment?.timeOfDay?.enabled === true,
            startHour: clamp(finite(raw?.environment?.timeOfDay?.startHour, 10), 0, 24),
            cycleSpeed: clamp(finite(raw?.environment?.timeOfDay?.cycleSpeed, 1), 0, 100),
        },
        weather: {
            type: environmentWeather,
            intensity: clamp(finite(raw?.environment?.weather?.intensity, terrain.weather.intensity), 0, 1),
        },
        special: asArray(raw?.environment?.special).map(str).filter(Boolean).slice(0, 8),
    };
    const rawUsed = raw?.scale?.usedBounds ?? {};
    const scale = {
        sourceEvidence: asArray(raw?.scale?.sourceEvidence).map(str).filter(Boolean).slice(0, 12),
        blockScale: str(raw?.scale?.blockScale) || '1 block ≒ 1 m',
        worldSize: { width: world.width, depth: world.depth, height: world.height },
        usableBounds: copy(world.usableBounds),
        usedBounds: {
            minX: clamp(integer(rawUsed.minX, world.usableBounds.minX), 0, world.width - 1),
            maxX: clamp(integer(rawUsed.maxX, world.usableBounds.maxX), 1, world.width),
            minY: clamp(integer(rawUsed.minY, world.usableBounds.minY), 0, world.height - 1),
            maxY: clamp(integer(rawUsed.maxY, Math.max(defaultY + 1, 20)), 1, world.height),
            minZ: clamp(integer(rawUsed.minZ, world.usableBounds.minZ), 0, world.depth - 1),
            maxZ: clamp(integer(rawUsed.maxZ, world.usableBounds.maxZ), 1, world.depth),
        },
        rationale: str(raw?.scale?.rationale),
    };
    const design = {
        formatVersion: WORLD_DESIGN_FORMAT_VERSION,
        mapId: startMapId,
        name: {
            ja: str(raw?.name?.ja) || localizedForGeneration(analysis.world.startMap.name, language, startMapId),
            en: str(raw?.name?.en) || localizedForGeneration(analysis.world.startMap.name, language, startMapId),
        },
        concept: str(raw?.concept) || analysis.world.startMap.concept,
        scale,
        foundation: {
            mode,
            baseTemplateId: template.id,
            selectionReason: str(raw?.foundation?.selectionReason),
            generator,
            inheritBaseFills,
            fills: foundationFills,
        },
        spawn,
        terrain,
        environment,
        areas,
        buildings,
        buildingClusters,
        landmarks,
        cityWall,
        detailFills,
        groves,
        npcPlacements,
        enemyPlacements,
        eventIdeas,
        notes: [...asArray(raw?.notes).map(str).filter(Boolean), ...autoNotes].slice(0, 24),
    };
    repairWorldDesignSurfaceAnchors(design);
    return design;
}

/** 人間の確認・依頼文への添付用に world-design.json を Markdown 化する。 */
export function renderWorldDesignMd(design) {
    const s = design.scale;
    const f = design.foundation;
    const lines = [
        `# 初期ワールド設計: ${design.name.ja}（${design.mapId}）`,
        '',
        `- 英語名: ${design.name.en}`,
        `- コンセプト: ${design.concept || '—'}`,
        `- 基盤: ${f.baseTemplateId} / ${f.mode} / generator=${f.generator}${f.selectionReason ? ` — ${f.selectionReason}` : ''}`,
        `- 縮尺: ${s.blockScale} / ワールド ${s.worldSize.width}×${s.worldSize.depth}×${s.worldSize.height}`,
        `- 使用範囲: x=${s.usedBounds.minX}..${s.usedBounds.maxX}, y=${s.usedBounds.minY}..${s.usedBounds.maxY}, z=${s.usedBounds.minZ}..${s.usedBounds.maxZ}`,
        `- 規模判断: ${s.rationale || '—'}`,
        `- スポーン: [${design.spawn.join(', ')}]`,
        `- 自然: ${design.terrain.features.length ? design.terrain.features.join('、') : '—'}`,
        `- 天候・空: ${design.environment.weather.type} / ${design.environment.skyMood || '—'} / ${design.environment.lightingMood || '—'}`,
        '',
        '## エリア構成',
        '',
        ...design.areas.map((area) => `- **${area.nameJa}**（${area.id} / ${area.type} / ${area.surfaceBlock} / priority ${area.priority}）: ${area.purpose || '—'} / ${area.boundsHint}`),
        '',
        '## 地形・生活インフラ',
        '',
        `- 標高: 下層 ${design.terrain.elevation.lowerCityHeight} / 上層 ${design.terrain.elevation.upperCityHeight} / 城 ${design.terrain.elevation.castleBaseHeight}（境界 Z=${design.terrain.boundaries.lowerUpperDivide}, ${design.terrain.boundaries.castleUpperDivide} / 大階段 ${design.terrain.stairs.length}）`,
        `- 河川・湖: ${design.terrain.water.rivers.length} / 用水路: ${design.terrain.water.canals.length} / 道路: ${design.terrain.roads.length}（範囲付き ${design.terrain.roads.filter((road) => road.range).length}）`,
        `- 森: ${design.terrain.vegetation.forestZones.length} / 木立: ${asArray(design.groves).length} / 花: ${design.terrain.vegetation.flowerZones.length}`,
        `- 基盤 fill: ${f.fills.length} / 景観・橋・庭等 fill: ${design.detailFills.length}`,
        ...design.detailFills.map((fill) => `- ${fill.id}: ${fill.purpose || fill.blockType}（${fill.shape} / ${fill.blockType}）`),
        ...asArray(design.groves).map((grove) => `- 木立 ${grove.id}: ${grove.purpose || '—'}（${grove.shape === 'rect' ? `${grove.bounds.minX},${grove.bounds.minZ}〜${grove.bounds.maxX},${grove.bounds.maxZ}` : `中心 ${grove.center.x},${grove.center.z} 半径 ${grove.radius}`} / ${grove.treeCount}本）`),
        '',
        '## シンボル建造物（landmarks）',
        '',
        ...(asArray(design.landmarks).length ? asArray(design.landmarks).map((landmark) => {
            if (landmark.kind === 'castle') return `- **${landmark.nameJa}**（castle / scale ${landmark.scale}）@Z=${landmark.positionZ}: ${landmark.purpose || '—'}`;
            if (landmark.kind === 'tower') return `- **${landmark.nameJa}**（tower / r${landmark.baseRadius}→${landmark.topRadius} × h${landmark.height} / ${landmark.topStyle} / ${landmark.materials.wall}）@[${landmark.position.join(', ')}]${landmark.altitude != null ? ` 高度${landmark.altitude}` : ''}: ${landmark.purpose || '—'}`;
            if (landmark.kind === 'blueprint') return `- **${landmark.nameJa}**（blueprint / ${landmark.blueprintId} ${landmark.size ? landmark.size.join('×') : ''} / 回転${landmark.rotation}°）@[${landmark.position.join(', ')}]${landmark.altitude != null ? ` 高度${landmark.altitude}` : ''}: ${landmark.purpose || '—'}`;
            return `- **${landmark.nameJa}**（custom / fill ${landmark.fills.length}個）@(${landmark.bounds.minX},${landmark.bounds.minZ})〜(${landmark.bounds.maxX},${landmark.bounds.maxZ}): ${landmark.purpose || '—'}`;
        }) : ['- （なし）']),
        ...(design.cityWall?.enabled ? ['', `- 都市外周壁: 半径 ${design.cityWall.position} / 高さ ${design.cityWall.height} / ${design.cityWall.wallBlockType}${design.cityWall.gate.enabled ? ' / 南門あり' : ''}`] : []),
        '',
        '## 主要建築物',
        '',
        ...(design.buildings.length ? design.buildings.map((building) => `- **${building.nameJa}**（${building.id} / ${building.width}×${building.depth}×${building.floors}階${building.wallBlock ? ` / ${building.wallBlock}` : ''}${building.flatRoof ? ' / 陸屋根' : ''}）@[${building.position.join(', ')}] ${building.areaId}: ${building.purpose || '—'}${building.surroundings.length ? ` / 周辺: ${building.surroundings.join('、')}` : ''}`) : ['- （なし）']),
        '',
        '## 建物群・生活密度',
        '',
        ...(design.buildingClusters.length ? design.buildingClusters.map((cluster) => `- **${cluster.id}** @${cluster.areaId}: ${cluster.count}棟 / ${cluster.buildingWidth}×${cluster.buildingDepth} / 間隔${cluster.spacing} / ${cluster.purpose || '—'}${cluster.surroundings.length ? ` / 周辺: ${cluster.surroundings.join('、')}` : ''}`) : ['- （なし）']),
        '',
        '## NPC 配置',
        '',
        ...(design.npcPlacements.length ? design.npcPlacements.map((entry) => `- ${entry.castId} @${entry.areaId} [${entry.position.join(', ')}]: ${entry.note || '—'}`) : ['- （なし）']),
        '',
        '## モンスター生息設計',
        '',
        ...(design.enemyPlacements.length ? design.enemyPlacements.map((entry) => `- ${entry.enabled ? '✓' : '×'} ${entry.name || entry.castId} @${entry.areaId} [${entry.spawnBounds.minX}..${entry.spawnBounds.maxX}, ${entry.spawnBounds.minZ}..${entry.spawnBounds.maxZ}]: ${entry.enemyRank} / 表示${entry.displayScale}倍 / ${entry.habitatType} / ${entry.behavior} / ${entry.groupSize}体 / 半径${entry.activityRadius}${entry.habitatRequirements.length ? ` / 必要環境: ${entry.habitatRequirements.join('、')}` : ''}${entry.note ? ` / ${entry.note}` : ''}`) : ['- （なし）']),
        '',
        '## イベント案',
        '',
        ...(design.eventIdeas.length ? design.eventIdeas.map((event) => {
            const location = event.trigger === 'proximity' ? ` [${event.position.join(', ')}] 半径${event.radius}` : '';
            const display = event.presentation === 'dialogue' ? `会話: ${asArray(event.lines).join(' / ')}` : `通知: ${event.message}`;
            return `- ${event.id}（${event.trigger} @${event.areaId}${location} / ${event.presentation} / ${event.once ? '一度だけ' : '再表示'}）: ${display}${event.storyBasis ? ` / 根拠: ${event.storyBasis}` : ''}`;
        }) : ['- （なし）']),
    ];
    if (s.sourceEvidence.length) lines.push('', '## 規模判断の根拠', '', ...s.sourceEvidence.map((entry) => `- ${entry}`));
    if (design.notes.length) lines.push('', '## 注意・手動作業メモ', '', ...design.notes.map((entry) => `- ${entry}`));
    lines.push('');
    return lines.join('\n');
}
